import React from 'react';

import {
	COUNTRY,
	CURRENCY,
	type EventExtraData,
	type HexAlpha,
	IS_LOCAL,
	InternalError,
	InvalidStateError,
	LANGUAGE,
	PAYMENT_METHOD,
	type REQ_SOURCE,
	type RESERVATION_SEAT_TYPE,
	type UUID,
	arrayOf,
	calcValue,
	cleanObject,
	getFullName,
	get as getObject,
	getTicketTypeExtraDataParsed,
	getTicketTypeGroupExtraDataParsed,
	getTicketTypeUserDataParsed,
	hexAlpha,
	isDefined,
	isEmpty,
	isObject,
	makeTime,
	mapDefined,
	noop,
	objectEntries,
	objectKeys,
	set as setObject,
	silenceError,
	sortBy,
	type ticketTypeExtraDataWithDefaultsSchema,
	type ticketTypeGroupExtraDataWithDefaultsSchema,
	type ticketTypeUserDataSchemaWithDefaults,
	valOrThrow,
} from '@nfctron/nexus-core';
import { type SvgIconComponent, useContextRequired, useFormat, useIsMounted, useToast } from '@nfctron/nexus-react';
import { ApplePayLogo, CzechiaFlagIcon, EuFlagIcon, GooglePayShortLogo, McClickToPayBlackLogo, McVisaLogo } from '@nfctron/nexus-react-svg';
import { type VenueMapTypes } from '@nfctron/nexus-tickets';

import * as Sentry from '@sentry/nextjs';
import { type QueryClient, useIsFetching, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ZustandHookSelectors, createSelectorHooks } from 'auto-zustand-selectors-hook';
import { formatDistanceToNow, isBefore } from 'date-fns';
import isDeepEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';
import { StarHalfIcon } from 'lucide-react';
import { useRouter } from 'next/router';
import { type FieldPath, type FieldPathValue } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { type StoreApi, type UseBoundStore, create, useStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { shallow as shallowEqual } from 'zustand/shallow';

import packageJson from '@root/config/package.json';

import { type ApiError, basePassApi } from '@root/features/api-client/api-client';
import { useApiClient } from '@root/features/api-client/api-client.context';
import {
	type FindAllExtraProductsFromTicketTypeResultDto,
	type FindAllProductGroupsFromTicketTypeResultDto,
	type GetExtraProductsFromEventCompanyResultDto,
	type GetExtraProductsFromEventProductResultDto,
	type GetTicketTypesFullResultDto,
	type OrderCreateCreditFromCodeBodyDto,
	type OrderCreateV2BodyDto,
	type PassApi,
} from '@root/features/api-client/generated/pass.api';
import { type UserAccount } from '@root/features/authentication/types/userAccount';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { type EVENT_TYPE } from '@root/lib/constants/eventType';
import { ITEM_TYPE } from '@root/lib/constants/itemType';
import { languageEnumTickets } from '@root/lib/constants/language';
import { useRouterParams } from '@root/lib/hooks/useRouterParams';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { OrderCreateErrorModal } from '@root/lib/modals/components/Order/OrderCreateError.modal';
import { ReservationErrorModal } from '@root/lib/modals/components/Reservation/ReservationError.modal';
import { openModal } from '@root/lib/modals/utils/openModal';
import { type TrackingModule } from '@root/lib/modules/tracking/tracking.module';
import { useTracking } from '@root/lib/modules/tracking/TrackingProvider';
import { appLogger } from '@root/lib/utils/logger';

import {
	getExtraProductDataOverride,
	getOrgPartnerDataOverride,
	getTicketProductGroupOverride,
	getTicketTypeDataOverride,
} from '../../../_resources/hooks/getDataOverride';

import { type CartMultiViewSlice, createCartMultiViewSlice } from './multiViewStore';

// TODO: move
const equalityPresetFns = { shallow: shallowEqual, deep: isDeepEqual, none: () => true } as const;
type EqualityPreset = keyof typeof equalityPresetFns;
type Equality<T> = ((a: T, b: T) => boolean) | EqualityPreset;
export function useEquality<S, U>(selector: (state: S) => U, equality: Equality<U | undefined> = 'shallow'): (state: S) => U {
	const prev = React.useRef<U>(undefined);
	return (state) => {
		const next = selector(state);
		const equalityFn = typeof equality === 'string' ? equalityPresetFns[equality] : equality;
		const isEqualRes = equalityFn(prev.current, next);
		if (isEqualRes) return prev.current ?? next;
		prev.current = next;
		return next;
	};
}

// TODO: move
export function getSaleStatus(params: {
	saleFrom: Date | null;
	saleTo: Date | null;
	defaultStatus?: 'active' | 'ended' | 'not-started' | 'not-set';
	now?: Date;
}) {
	const now = params.now ?? new Date();
	const defaultStatus = params.defaultStatus ?? 'not-set';

	/** if no sale dates are set, ticket type is always active */
	if (!isDefined(params.saleFrom) && !isDefined(params.saleTo)) return 'active';

	/** if both sale dates are set and current date is within the interval, ticket type is active */
	if (isDefined(params.saleFrom) && isDefined(params.saleTo)) {
		/** if current date is before saleFrom, ticket type is not started */
		if (isBefore(now, params.saleFrom)) return 'not-started';
		/** if current date is after saleTo, ticket type has ended */
		if (isBefore(params.saleTo, now)) return 'ended';
		/** if current date is after saleFrom and before saleTo, ticket type is active */
		return 'active';
	}

	/** if saleFrom date is set (and saleTo is not) */
	if (isDefined(params.saleFrom)) {
		/** if current date is before saleFrom, ticket type is not started */
		if (isBefore(now, params.saleFrom)) return 'not-started';
		/** if current date is after saleFrom, ticket type is active */
		return 'active';
	}

	/** if saleTo date is set (and saleFrom is not) */
	if (isDefined(params.saleTo)) {
		/** if current date is after saleTo, ticket type has ended */
		if (isBefore(params.saleTo, now)) return 'ended';
		/** if current date is before saleTo, ticket type is active */
		return 'active';
	}

	/** if none of the above conditions are met, ticket type is default (not-set) */
	return defaultStatus;
}

export function getCartTicketSaleState(params: {
	countdownDate: Date | null;
	saleFrom: Date | null;
	saleTo: Date | null;
	ticketsSet: boolean;
	hasPaymentMethods: boolean;
}) {
	const now = new Date();
	const saleStatus = getSaleStatus({ saleFrom: params.saleFrom, saleTo: params.saleTo, defaultStatus: 'active' });
	const countdownStatus = isDefined(params.countdownDate)
		? getSaleStatus({ saleFrom: now, saleTo: params.countdownDate, defaultStatus: 'not-set' })
		: 'not-set';
	const isTicketsSet = params.ticketsSet === true || saleStatus === 'active';
	const isTicketSaleEnabled = (isDefined(params.countdownDate) ? countdownStatus !== 'active' : isTicketsSet) && params.hasPaymentMethods;

	return { saleStatus, countdownStatus, isTicketsSet, isTicketSaleEnabled };
}

export const cartStoreQueryKeys = {
	getTicketTypesApiQueryKey: (eventId: UUID, locale: string = 'cs', promoUrlQuery: UsePromoCodesApiQuery | null = null) =>
		[basePassApi.v2Event.getTicketTypesFromEventFull.id, { eventId, locale }, promoUrlQuery] as const,
	getTicketImportApiQueryKey: (eventId: UUID, ticketCode: string) => [basePassApi.v2Event.findTicketByCode.id, { eventId, ticketCode }] as const,
	getExtraProductsApiQueryKey: (eventId: UUID, locale: string = 'cs', cartedTicketTypeIds: UUID[]) =>
		[basePassApi.v2Ticket.findExtraProductsFromTicketType.id, { ticketTypes: cartedTicketTypeIds }] as const,
	getVenueApiQueryKey: (eventId: UUID, locale: string = 'cs') => [basePassApi.v2Event.findOneVenueWithSectors.id, { eventId }] as const,
	getSectorApiQueryKey: (sectorId: UUID, locale: string = 'cs') => [basePassApi.reservation.findOneSector.id, { sectorId }] as const,
	getCategoriesApiQueryKey: (eventId: UUID, locale: string = 'cs') =>
		[basePassApi.v2Event.findAllReservationCategoriesFromEvent.id, { eventId }] as const,
	getMaximusProductsApiQueryKey: (eventId: UUID, locale: string = 'cs') =>
		[basePassApi.v2Event.getExtraProductsFromEvent.id, { eventId, locale }] as const,
} as const;

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Cart Config
 * -----------------------------------------------------------------------------------------------*/
export namespace CartStoreConfigSlice {
	export interface CartConfig {
		eventId: UUID;
		currencyId: CURRENCY;
		eventTypeId: EVENT_TYPE;
		reqSourceId: REQ_SOURCE.WEB | REQ_SOURCE.WEB_DINO_LIVE | REQ_SOURCE.WEB_UNITED_TICKETS;
		isDemoCart: boolean;
		isReservationVenue: boolean;
		isEntryQueuePrevented: boolean;
		isEntryQueueActive: boolean;
		isTicketHolderNameRequired: boolean;
		isTicketEmptyChipsGenerated: boolean;
		saleInfo: { ticketsSet: boolean; saleFrom: Date | null; saleTo: Date | null; countdownDate: Date | null };
		importProviders: { nfctron: EventExtraData['nfctronImportProvider']; goout: EventExtraData['gooutImportProvider'] };
		eventContext: { eventGroups: Array<{ namePub: string }>; eventLocation: string | undefined; eventPlace: string; eventName: string } | null;
	}

	export interface State {
		cartConfig: CartConfig;
		queryClient: QueryClient;
	}

	export interface Actions {}

	export interface Store extends State, Actions {}

	export interface Selectors {
		getCartState: (state: CartStore.Store) => {
			countdownStatus: ReturnType<typeof getSaleStatus>;
			saleStatus: ReturnType<typeof getSaleStatus>;
			isTicketsSet: boolean;
			isTicketSaleEnabled: boolean;
			isTicketSaleEnabledOrHasTickeTypes: boolean;
			isTicketImportEnabled: boolean;
			isPhysicalTicketTopUpEnabled: boolean;
			isCartEnabled: boolean;
		};
	}
}

const cartStoreConfigSliceSelectors = {
	getCartState(state) {
		const { cartConfig } = state;
		const isPhysicalTicketTopUpEnabled = isDefined(cartConfig.importProviders.nfctron);
		const isTicketImportEnabled = isDefined(cartConfig.importProviders.goout);
		const { saleStatus, countdownStatus, isTicketsSet, isTicketSaleEnabled } = getCartTicketSaleState({
			countdownDate: cartConfig.saleInfo.countdownDate,
			saleFrom: cartConfig.saleInfo.saleFrom,
			saleTo: cartConfig.saleInfo.saleTo,
			ticketsSet: cartConfig.saleInfo.ticketsSet,
			hasPaymentMethods: state.paymentOptionsConfig.length > 0,
		});
		const isTicketSaleEnabledOrHasTickeTypes = (isTicketSaleEnabled && cartConfig.saleInfo.ticketsSet) || state.ticketTypesConfig.length > 0;
		const isCartEnabled = isTicketSaleEnabled || isTicketImportEnabled || isPhysicalTicketTopUpEnabled;

		return {
			isTicketSaleEnabled: isTicketSaleEnabledOrHasTickeTypes,
			isTicketSaleEnabledOrHasTickeTypes,
			isPhysicalTicketTopUpEnabled,
			isTicketImportEnabled,
			saleStatus,
			countdownStatus,
			isTicketsSet,
			isCartEnabled,
		};
	},
} as const satisfies CartStoreConfigSlice.Selectors;

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Payment Methods
 * -----------------------------------------------------------------------------------------------*/
export namespace CartStorePaymentOptionsSlice {
	export enum PAYMENT_OPTION_ID {
		CREDIT_CARD = PAYMENT_METHOD.CREDIT_CARD,
		APPLE_PAY = PAYMENT_METHOD.APPLE_PAY,
		GOOGLE_PAY = PAYMENT_METHOD.GOOGLE_PAY,
		CLICK_TO_PAY = PAYMENT_METHOD.CLICK_TO_PAY,
		BANK_TRANSFER_CZK = 100,
		BANK_TRANSFER_EUR = 101,
	}

	export interface PaymentOptionConfigBase {
		paymentOptionId: PAYMENT_OPTION_ID;
		displayOrder: number;
		Icon: SvgIconComponent | null;
	}

	export interface PaymentOptionConfigBasic extends PaymentOptionConfigBase {
		paymentMethodId: PAYMENT_METHOD.CREDIT_CARD | PAYMENT_METHOD.APPLE_PAY | PAYMENT_METHOD.GOOGLE_PAY | PAYMENT_METHOD.CLICK_TO_PAY;
		bankAccount?: never;
	}

	export interface PaymentOptionConfigBankTransfer extends PaymentOptionConfigBase {
		paymentMethodId: PAYMENT_METHOD.BANK_TRANSFER;
		bankAccount: { bankAccountId: UUID; currencyId: CURRENCY };
	}

	export type PaymentOptionConfig = PaymentOptionConfigBasic | PaymentOptionConfigBankTransfer;

	export type PaymentOptionListed = PaymentOptionConfig & { disabled: boolean; listed: boolean };

	export interface PaymentOptionPreset extends Omit<PaymentOptionConfig, 'bankAccount'> {
		currencyIdSupported: Array<CURRENCY>;
	}

	export interface State {
		paymentOptionsConfig: Array<PaymentOptionConfig>;
		activePaymentOptionId: PAYMENT_OPTION_ID | null;
	}

	export interface Actions {
		loadPaymentOptionsConfig: (params: { paymentOptions: Array<PaymentOptionConfig> }) => void;
		setActivePaymentOptionId: (params: { paymentOptionId: PAYMENT_OPTION_ID | null }) => void;
		getActivePaymentOption: () => PaymentOptionConfig | null;
	}

	export interface Store extends State, Actions {}

	export interface Selectors {
		listPaymentOptions: (state: CartStore.Store) => Array<PaymentOptionListed>;
	}
}

const cartStorePaymentOptionsSelectors = {
	listPaymentOptions(state) {
		const restrictedToPaymentMethodsFromTicketTypes = state.getCartedTicketTypes().reduce<PAYMENT_METHOD[]>((acc, ticketType) => {
			if (ticketType.extraData.promotionCampaign?.allowedPaymentMethods) {
				acc.push(...ticketType.extraData.promotionCampaign.allowedPaymentMethods.map((pm) => pm.valueOf()));
			}
			return acc;
		}, []);

		return state.paymentOptionsConfig
			.map((_paymentOptionConfig) => {
				/** NOTE: we cannot use { ...paymentMethodConfig } here, because it would reate a new object each time and cause unnecessary re-renders */
				const paymentOptionConfig = _paymentOptionConfig as CartStorePaymentOptionsSlice.PaymentOptionListed;
				switch (paymentOptionConfig.paymentOptionId) {
					case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.CREDIT_CARD: {
						paymentOptionConfig.listed = true;
						paymentOptionConfig.disabled = false;
						break;
					}
					case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_EUR:
					case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_CZK: {
						const isBankTransferEnabled = isDefined(paymentOptionConfig.bankAccount);
						paymentOptionConfig.listed = isBankTransferEnabled;
						paymentOptionConfig.disabled = !isBankTransferEnabled;
						break;
					}
					case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.APPLE_PAY: {
						const isApplePayAvailable = typeof window !== 'undefined' && 'ApplePaySession' in window;
						paymentOptionConfig.listed = isApplePayAvailable;
						paymentOptionConfig.disabled = !isApplePayAvailable;
						break;
					}
					case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.GOOGLE_PAY: {
						paymentOptionConfig.listed = true;
						paymentOptionConfig.disabled = false;
						break;
					}
					case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.CLICK_TO_PAY: {
						paymentOptionConfig.listed = true;
						paymentOptionConfig.disabled = false;
						break;
					}
					default: {
						paymentOptionConfig.listed = false;
						paymentOptionConfig.disabled = true;
						break;
					}
				}

				/** keep listed only methods that are strictly allowed when restricted by some carted ticket type (e.g., allow only CTP for CTP-campaign ticket types) */
				if (restrictedToPaymentMethodsFromTicketTypes.length > 0) {
					paymentOptionConfig.listed = restrictedToPaymentMethodsFromTicketTypes.includes(paymentOptionConfig.paymentMethodId);
				}

				return paymentOptionConfig;
			})
			.filter((paymentOption) => paymentOption.listed)
			.sort(sortBy('displayOrder', { direction: 'asc' }));
	},
} as const satisfies CartStorePaymentOptionsSlice.Selectors;

/** payment options presets */
const paymentOptionsPresets = [
	{
		paymentOptionId: CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_CZK,
		paymentMethodId: PAYMENT_METHOD.BANK_TRANSFER,
		currencyIdSupported: [CURRENCY.CZK],
		displayOrder: 1,
		Icon: CzechiaFlagIcon,
	},
	{
		paymentOptionId: CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_EUR,
		paymentMethodId: PAYMENT_METHOD.BANK_TRANSFER,
		currencyIdSupported: [CURRENCY.EUR],
		displayOrder: 2,
		Icon: EuFlagIcon,
	},
	{
		paymentOptionId: CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.CLICK_TO_PAY,
		paymentMethodId: PAYMENT_METHOD.CLICK_TO_PAY,
		currencyIdSupported: [CURRENCY.CZK, CURRENCY.EUR, CURRENCY.GBP, CURRENCY.PLN, CURRENCY.RSD],
		displayOrder: 3,
		Icon: McClickToPayBlackLogo,
	},
	{
		paymentOptionId: CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.CREDIT_CARD,
		paymentMethodId: PAYMENT_METHOD.CREDIT_CARD,
		currencyIdSupported: [CURRENCY.CZK, CURRENCY.EUR, CURRENCY.GBP, CURRENCY.PLN, CURRENCY.RSD],
		displayOrder: 4,
		Icon: McVisaLogo,
	},
	{
		paymentOptionId: CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.GOOGLE_PAY,
		paymentMethodId: PAYMENT_METHOD.GOOGLE_PAY,
		currencyIdSupported: [CURRENCY.CZK, CURRENCY.EUR, CURRENCY.GBP, CURRENCY.PLN, CURRENCY.RSD],
		displayOrder: 5,
		Icon: GooglePayShortLogo,
	},
	{
		paymentOptionId: CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.APPLE_PAY,
		paymentMethodId: PAYMENT_METHOD.APPLE_PAY,
		currencyIdSupported: [CURRENCY.CZK, CURRENCY.EUR, CURRENCY.GBP, CURRENCY.PLN, CURRENCY.RSD],
		displayOrder: 6,
		Icon: ApplePayLogo,
	},
] as const satisfies Array<CartStorePaymentOptionsSlice.PaymentOptionPreset>;

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Ticket Types
 * -----------------------------------------------------------------------------------------------*/
export namespace CartStoreTicketTypesSlice {
	export interface TicketTypeGroupConfig {
		ticketTypeGroupId: UUID;
		namePub: string;
		displayOrder: number;
		extraData: z.infer<typeof ticketTypeGroupExtraDataWithDefaultsSchema>;
	}

	export interface TicketTypeCategoryConfig {
		categoryId: UUID;
	}

	export interface TicketTypeExtraProductConfig {
		ticketProductId: UUID;
		quantityLimit: number;
	}

	export interface TicketTypeChipConfig {
		productItemId: UUID;
	}

	export interface TicketTypeContentProductItemConfig {
		quantity: number;
		itemTypeId: ITEM_TYPE;
		productItemId: string;
	}

	export interface TicketTypeOrgPartnerConfig {
		namePub: string;
		logoUrl?: string;
		shortCode: string;
		benefitCode?: string;
		/** TODO: from orgPartner.extraData */
		orgPartnerBackgroundColor?: string;
		orgPartnerForegroundColor?: string;
	}

	export interface TicketTypeConfig {
		ticketTypeId: UUID;
		eventId: UUID;
		namePub: string;
		description: string | null;
		displayOrder: number;
		price: number;
		/** ticket order limits */
		ticketLimitOrder: number;
		/** credit top up limits */
		creditLimit: number;
		creditMinimum: number;
		/** available ticket count */
		ticketsAvailable: number;
		/** sale info */
		saleStatus: ReturnType<typeof getSaleStatus>;
		saleFrom: Date | null;
		saleTo: Date | null;
		/** extra user data fields configuration */
		userData: z.infer<typeof ticketTypeUserDataSchemaWithDefaults> | null;
		/** extraData */
		extraData: z.infer<typeof ticketTypeExtraDataWithDefaultsSchema>;
		/** within a ticket type group */
		ticketTypeGroup: TicketTypeGroupConfig | null;
		/** content within the ticket type */
		contentProductItems: Array<TicketTypeContentProductItemConfig>;
		/** extra products available for sale */
		extraProducts: Array<TicketTypeExtraProductConfig>;
		/** content chips */
		chips: Array<TicketTypeChipConfig>;
		/** reservation (seating) categories */
		categories: Array<TicketTypeCategoryConfig>;
		/** from an organizer partner (promo code) */
		orgPartner: TicketTypeOrgPartnerConfig | null;
		/** states */
		isHolderRequired: boolean;
		isReservable: boolean;
		/** internal indicators */
		__isListable: boolean;
		__isImported: boolean;
	}

	export interface State {
		ticketTypesConfig: Array<TicketTypeConfig>;
		autoCartTicketType: { config: TicketTypeConfig; isOnlyOne: boolean } | null;
	}

	export interface Actions {
		loadTicketTypesConfig: (params: {
			ticketTypes: Array<TicketTypeConfig>;
			duplicatesStrategy: 'ignore' | 'replace';
			forceOverwrite: boolean;
			shouldAutoCart: boolean;
		}) => void;
		insertTicketTypeToConfig: (params: { ticketType: Array<TicketTypeConfig>; duplicatesStrategy: 'ignore' | 'replace' }) => void;
		removeTicketTypesFromConfig: (params: { ticketTypeIds: UUID[]; trackingModule: TrackingModule | null }) => void;
		getTicketTypeById: (params: { ticketTypeId: UUID }) => TicketTypeConfig;
		getTicketTypeStateById: (params: { ticketTypeId: UUID }) => { orderLimited: boolean; soldOut: boolean };
		hasTicketTypeExtraProducts: (
			params: { ticketTypeId: UUID; ticketType?: never } | { ticketTypeId?: never; ticketType: TicketTypeConfig },
		) => boolean;
		hasTicketTypeChips: (params: { ticketTypeId: UUID; ticketType?: never } | { ticketTypeId?: never; ticketType: TicketTypeConfig }) => boolean;
	}

	export interface Store extends State, Actions {}

	export interface Selectors {
		listTicketTypesForGroup: (state: CartStore.Store, params: { ticketTypeGroupId: UUID }) => Array<TicketTypeConfig>;
		listTicketTypesForCategory: (state: CartStore.Store, params: { categoryId: UUID }) => Array<TicketTypeConfig>;
		listTicketTypes: (state: CartStore.Store) => Array<TicketTypeConfig>;
		listOrgPartners: (state: CartStore.Store) => Array<TicketTypeOrgPartnerConfig>;
	}
}

const cartStoreTicketTypesSelectors = {
	listTicketTypesForGroup(state, { ticketTypeGroupId }) {
		const ticketTypesConfig = state.ticketTypesConfig;
		return ticketTypesConfig
			.filter((tt) => tt.ticketTypeGroup?.ticketTypeGroupId === ticketTypeGroupId)
			.sort(sortBy('displayOrder', { direction: 'asc' }));
	},
	listTicketTypesForCategory(state, { categoryId }) {
		const ticketTypesConfig = state.ticketTypesConfig;
		return ticketTypesConfig
			.filter((tt) => tt.categories.some((c) => c.categoryId === categoryId))
			.sort(sortBy('displayOrder', { direction: 'asc' }));
	},
	listTicketTypes(state) {
		const ticketTypesConfig = state.ticketTypesConfig;
		return (
			ticketTypesConfig
				/** hide imported tickets and other unlistable ones */
				.filter((ticketType) => ticketType.__isListable && !ticketType.__isImported)
				/** sort by displayOrder */
				.sort(sortBy('displayOrder', { direction: 'asc' }))
		);
	},
	listOrgPartners(state) {
		const ticketTypesConfig = state.ticketTypesConfig;
		return ticketTypesConfig.reduce<CartStoreTicketTypesSlice.TicketTypeOrgPartnerConfig[]>((acc, ticketType) => {
			if (isDefined(ticketType.orgPartner) && !acc.some((op) => op.shortCode === ticketType.orgPartner?.shortCode)) {
				acc.push(ticketType.orgPartner);
			}
			return acc;
		}, []);
	},
} as const satisfies CartStoreTicketTypesSlice.Selectors;

interface RawTicketTypeData extends GetTicketTypesFullResultDto {
	saleFrom: string | null;
	saleTo: string | null;
}
export interface RawTicketTypeOptions {
	eventId: UUID;
	isTicketHolderNameRequired: boolean;
	isTicketEmptyChipsGenerated: boolean;
	orgPartnerShortCode?: string;
	orgPartnerBenefitCode?: string;
}
export function createTicketTypeConfig(ticketType: RawTicketTypeData, options: RawTicketTypeOptions): CartStoreTicketTypesSlice.TicketTypeConfig {
	/** get ticket sale status */
	const saleStatus = getSaleStatus({
		saleFrom: mapDefined(ticketType.saleFrom, (date) => new Date(date)) ?? null,
		saleTo: mapDefined(ticketType.saleTo, (date) => new Date(date)) ?? null,
		defaultStatus: 'active',
	});

	/** construct ticket type config base */
	const base = {
		ticketTypeId: ticketType.ticketTypeId,
		eventId: options.eventId,
		namePub: ticketType.namePub,
		description: ticketType.description,
		displayOrder: ticketType.displayOrder,
		price: ticketType.price,
		ticketLimitOrder: ticketType.ticketLimitOrder,
		creditLimit: ticketType.creditLimit,
		creditMinimum: ticketType.creditMinimum,
		ticketsAvailable: ticketType.ticketsAvailable,
		categories: ticketType.categories,
		contentProductItems: ticketType.contentItems,
		extraProducts: ticketType.extraProducts,
		ticketTypeGroup:
			mapDefined(ticketType.ticketTypeGroup, (group) => ({
				...group,
				extraData: getTicketTypeGroupExtraDataParsed(group.extraData).withDefaults,
			})) ?? null,
		orgPartner:
			mapDefined(ticketType.orgPartner, (orgPartner) => ({
				...orgPartner,
				namePub: orgPartner.namePub,
				logoUrl: orgPartner.logoUrl,
				benefitCode: options.orgPartnerBenefitCode,
				shortCode: valOrThrow(options.orgPartnerShortCode, 'cannot create this ticket type config without orgPartnerShortCode'),
			})) ?? null,
		saleFrom: mapDefined(ticketType.saleFrom, (date) => new Date(date)) ?? null,
		saleTo: mapDefined(ticketType.saleTo, (date) => new Date(date)) ?? null,
		saleStatus,
		userData: getTicketTypeUserDataParsed(ticketType.userData).withDefaults,
		extraData: getTicketTypeExtraDataParsed(ticketType.extraData).withDefaults,
		isHolderRequired: options.isTicketHolderNameRequired,
		isReservable: ticketType.categories.length > 0,
		chips: [] as CartStoreTicketTypesSlice.TicketTypeChipConfig[],
		__isListable: true,
		__isImported: false,
	} satisfies CartStoreTicketTypesSlice.TicketTypeConfig;

	const withHardOverrides = getTicketTypeDataOverride(base);

	/** generate chips if top-up enabled (creditLimit > creditMinimum) or explicitly allowed */
	const isTopUpEnabled = withHardOverrides.creditLimit > withHardOverrides.creditMinimum;
	if (isTopUpEnabled || options.isTicketEmptyChipsGenerated) {
		withHardOverrides.chips = ticketType.contentItems.reduce<CartStoreTicketTypesSlice.TicketTypeChipConfig[]>((acc, chip) => {
			if (chip.itemTypeId === ITEM_TYPE.CHIP) {
				acc.push({ productItemId: chip.productItemId });
			}
			return acc;
		}, []);
	}

	return withHardOverrides;
}

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Ticket Type Groups
 * -----------------------------------------------------------------------------------------------*/
namespace CartStoreTicketTypeGroupsSlice {
	export interface TicketTypeGroupConfig {
		ticketTypeGroupId: UUID;
		namePub: string;
		displayOrder: number;
		extraData: z.infer<typeof ticketTypeGroupExtraDataWithDefaultsSchema>;
	}

	export interface State {
		ticketTypesListedGroupedOpen: Record<string, boolean>;
	}

	export interface Actions {
		toggleTicketTypeGroup: (params: { ticketTypeGroupId: string; changeTo: boolean; autoCloseOthers: boolean }) => void;
	}

	export interface Store extends State, Actions {}

	export interface Selectors {
		listTicketTypesGrouped: (state: CartStore.Store) => Array<{
			ticketTypeGroup: CartStoreTicketTypesSlice.TicketTypeGroupConfig | null;
			ticketTypes: Array<CartStoreTicketTypesSlice.TicketTypeConfig>;
		}>;
	}
}

const cartStoreTicketTypeGroupsSelectors = {
	listTicketTypesGrouped(state) {
		const ticketTypesListed = cartStoreTicketTypesSelectors.listTicketTypes(state);
		return ticketTypesListed
			.reduce<ReturnType<CartStore.Selectors['listTicketTypesGrouped']>>((acc, ticketType) => {
				const group = ticketType.ticketTypeGroup ?? null;
				const groupIndex = acc.findIndex((g) => g.ticketTypeGroup?.ticketTypeGroupId === group?.ticketTypeGroupId);
				if (groupIndex === -1) {
					acc.push({ ticketTypeGroup: group, ticketTypes: [ticketType] });
				} else {
					acc[groupIndex]?.ticketTypes.push(ticketType);
				}
				return acc;
			}, [])
			.sort(sortBy((i) => i.ticketTypeGroup?.displayOrder ?? 999, { direction: 'asc' }));
	},
} as const satisfies CartStoreTicketTypeGroupsSlice.Selectors;

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Seating
 * -----------------------------------------------------------------------------------------------*/ /*  */
namespace CartStoreSeatingSlice {
	export interface SeatConfig {
		seatId: UUID;
		sectorId: UUID;
		categoryId: UUID;
		fullName: string;
		seatRow: string;
		place: number;
		seatTypeId: RESERVATION_SEAT_TYPE;
		capacityLeft: number;
		label: string;
		position: { x: number; y: number; rotation: number };
		reservationSeatId: UUID | null;
		ticketTypesAvailable: number;
		unsalable: boolean;
	}

	export interface CrowdConfig {
		crowdId: UUID;
		sectorId: UUID;
		categoryId: UUID;
		name: string;
		tag: string;
		description?: string;
		capacityLeft: number;
		reservationCrowdId: UUID | null;
		ticketTypesAvailable: number;
	}

	export interface Reservation {
		reservationId: UUID;
		created: Date;
		reservedUntil?: Date;
		crowds: ReservationCrowd[];
		seats: ReservationSeat[];
	}

	export interface ReservationCrowd {
		reservationCrowdId: UUID;
		crowdId: UUID;
	}

	export interface ReservationSeat {
		reservationSeatId: UUID;
		seatId: UUID;
	}

	export interface SectorConfig extends VenueConfigSector {
		drawing: string;
	}

	export interface VenueConfigSector extends VenueMapTypes.VenueSector {}

	export interface VenueConfig extends VenueMapTypes.VenueData {
		sectors: Array<VenueConfigSector>;
	}

	export interface CategoryConfig {
		categoryId: UUID;
		name: string;
		description?: string;
		color: HexAlpha;
		ticketTypesAvailable: number;
	}

	export interface State {
		/** venue */
		venueConfig: VenueConfig | null;
		/** categories */
		categoriesConfig: Array<CategoryConfig>;
		/** sectors */
		sectorsConfig: Array<SectorConfig>;
		openedSectorId: UUID | null;
		/** seats */
		seatsConfig: Array<SeatConfig>;
		selectedSeatId: UUID | null;
		/** crowds */
		crowdsConfig: Array<CrowdConfig>;
		selectedCrowdId: UUID | null;
		/** reservation */
		seatingReservation: Reservation | null;
	}

	export interface Actions {
		/** venue */
		loadVenueConfig: (params: { venue: VenueConfig }) => void;
		/** categories */
		loadCategoriesConfig: (params: { categories: Array<CategoryConfig>; duplicatesStrategy: 'ignore' | 'replace'; forceOverwrite: boolean }) => void;
		getCategoryById: (params: { categoryId: UUID }) => CategoryConfig;
		getCategoryTicketTypes: (params: { categoryId: UUID }) => Array<CartStoreTicketTypesSlice.TicketTypeConfig>;
		/** sectors */
		loadSectorsConfig: (params: { sectors: Array<SectorConfig>; duplicatesStrategy: 'ignore' | 'replace'; forceOverwrite: boolean }) => void;
		openSector: (params: { sectorId: UUID }) => void;
		closeSector: () => void;
		getSectorById: (params: { sectorId: UUID }) => SectorConfig;
		getSectorByIdWithSeatsAndCrowds: (params: { sectorId: UUID }) => SectorConfig & { seats: SeatConfig[]; crowds: CrowdConfig[] };
		getSectorStateById: (params: { sectorId: UUID }) => { soldOut: boolean; opened: boolean };
		/** seats */
		loadSeatsConfig: (params: { seats: Array<SeatConfig>; duplicatesStrategy: 'ignore' | 'replace'; forceOverwrite: boolean }) => void;
		selectSeat: (params: { seatId: UUID }) => void;
		unselectSeat: () => void;
		getSeatById: (params: { seatId: UUID }) => SeatConfig;
		getSeatStateById: (params: { seatId: UUID }) => { soldOut: boolean; saleable: boolean; unavailable: boolean; selected: boolean; carted: boolean };
		getSeatFromCartedTicket: (params: { cartTicketId: UUID }) => SeatConfig | null;
		getCartedTicketFromSeat: (params: { seatId: UUID }) => CartStoreCartedTicketsSlice.CartedTicket | null;
		/** crowds */
		loadCrowdsConfig: (params: { crowds: Array<CrowdConfig>; duplicatesStrategy: 'ignore' | 'replace'; forceOverwrite: boolean }) => void;
		selectCrowd: (params: { crowdId: UUID }) => void;
		unselectCrowd: () => void;
		getCrowdById: (params: { crowdId: UUID }) => CrowdConfig;
		getCrowdStateById: (params: { crowdId: UUID }) => {
			soldOut: boolean;
			saleable: boolean;
			unavailable: boolean;
			selected: boolean;
			carted: boolean;
		};
		getCrowdFromCartedTicket: (params: { cartTicketId: UUID }) => CrowdConfig | null;
		getCartedTicketsFromCrowd: (params: { crowdId: UUID }) => Array<CartStoreCartedTicketsSlice.CartedTicket>;
		/** reservation */
		createSeatingReservation: (params: { reservation: Reservation }) => void;
		addSeatToSeatingReservation: (params: { seatId: UUID; reservationSeatId: UUID }) => void;
		removeSeatFromSeatingReservation: (params: { seatId: UUID }) => void;
		addCrowdToSeatingReservation: (params: { crowdId: UUID; reservationCrowdId: UUID }) => void;
		removeCrowdFromSeatingReservation: (params: { crowdId: UUID }) => void;
		clearSeatingReservation: () => void;
	}

	export interface Store extends State, Actions {}

	export interface Selectors {
		listCategories: (state: CartStore.Store) => Array<CategoryConfig>;
	}
}

const cartStoreSeatingSelectors = {
	listCategories(state) {
		return state.categoriesConfig.sort(sortBy('categoryId', { direction: 'asc' }));
	},
} as const satisfies CartStoreSeatingSlice.Selectors;

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Extra Products
 * -----------------------------------------------------------------------------------------------*/
export namespace CartStoreExtraProductsSlice {
	export enum PRODUCT_CONTROL_TYPE {
		SIMPLE = 0,
		GROUP = 1,
	}

	export interface ExtraProductConfig {
		eventId: UUID;
		ticketProductId: UUID;
		productsAvailable: number;
		name: string;
		description?: string;
		price: number;
		displayOrder: number;
		optionalTextRegex?: string;
		gallery?: ExtraProductGallery;
		productGroupId?: UUID;
		productItems: Array<{ quantity: number; itemTypeId: ITEM_TYPE; productItemId: UUID }>;
		variants: ExtraProductConfigVariantCombo<number>;
	}

	export type ExtraProductConfigVariantCombo<TVariantValue extends number | null = number | null> = Record<UUID, TVariantValue>;

	export interface ExtraProductGallery {
		galleryId: UUID;
		name: string;
		description?: string;
		images: ExtraProductGalleryImage[];
	}

	export interface ExtraProductGalleryImage {
		imageId: UUID;
		name: string;
		description?: string;
		imageUrl: string;
	}

	export interface ExtraProductGroupConfig {
		eventId: UUID;
		productGroupId: UUID;
		name: string;
		description?: string;
		gallery?: ExtraProductGallery;
		variants: Array<ExtraProductGroupConfigVariant>;
		/** add quantityLimit per group (not per product) - allow only 1 product from the group */
		quantityLimit: number;
		displayOrder: number;
	}

	export type ExtraProductGroupConfigVariant = {
		variantId: UUID;
		name: string;
		description?: string;
		controlTypeId: PRODUCT_GROUP_VARIANT_CONTROL_TYPE;
		displayOrder: number;
		gallery?: ExtraProductGallery;
		values: ExtraProductGroupVariantValue[];
	};

	export enum PRODUCT_GROUP_VARIANT_CONTROL_TYPE {
		RADIO = 1,
		DROPDOWN = 2,
		SLIDER = 3,
	}

	export type ExtraProductGroupVariantValue = { valueId: number; name: string; selected: boolean };

	export interface State {
		extraProductsConfig: Array<ExtraProductConfig>;
		extraProductGroupsConfig: Array<ExtraProductGroupConfig>;

		extraProductConfigSheetProductInput: { cartTicketId: UUID; ticketProductId: UUID; cartProductId?: UUID } | null;
		extraProductConfigSheetVariantsInput: { cartTicketId: UUID; productGroupId: UUID; cartProductId?: UUID } | null;
	}

	export interface Actions {
		loadExtraProductsConfig: (params: {
			extraProducts: Array<ExtraProductConfig>;
			duplicatesStrategy: 'ignore' | 'replace';
			forceOverwrite: boolean;
		}) => void;
		loadExtraProductGroupsConfig: (params: {
			extraProductGroups: Array<ExtraProductGroupConfig>;
			duplicatesStrategy: 'ignore' | 'replace';
			forceOverwrite: boolean;
		}) => void;
		getExtraProductById: (params: { ticketProductId: UUID }) => ExtraProductConfig;
		getExtraProductByIdWithQuantityLimit: (params: { ticketProductId: UUID; ticketTypeId: UUID }) => ExtraProductConfig & { quantityLimit: number };
		getExtraProductChipCount: (params: { ticketProductId: UUID; ticketTypeId: UUID }) => number;
		getExtraProductInitialChips: (params: { ticketProductId: UUID; ticketTypeId: UUID }) => Array<CartStoreCartedTicketsSlice.CartedTicketChip>;
		getExtraProductGroupById: (params: { productGroupId: UUID }) => ExtraProductGroupConfig;
		getExtraProductGroupProducts: (params: { productGroupId: UUID; ticketTypeId: UUID }) => Array<ExtraProductConfig & { quantityLimit: number }>;
		getExtraProductGroupPriceRange: (params: { productGroupId: UUID; ticketTypeId: UUID }) => { min: number; max: number };
		getExtraProductGroupStateById: (params: { productGroupId: UUID; cartTicketId: UUID }) => { quantityLimited: boolean; soldOut: boolean };
		getSimpleProductControls: (params: {
			ticketTypeId: UUID;
		}) => Array<{ type: PRODUCT_CONTROL_TYPE.SIMPLE; displayOrder: number; ticketProductId: UUID }>;
		getProductGroupControls: (params: {
			ticketTypeId: UUID;
		}) => Array<{ type: PRODUCT_CONTROL_TYPE.GROUP; displayOrder: number; productGroupId: UUID }>;
		getProductControls: (params: {
			ticketTypeId: UUID;
		}) => Array<
			| { type: PRODUCT_CONTROL_TYPE.SIMPLE; displayOrder: number; ticketProductId: UUID }
			| { type: PRODUCT_CONTROL_TYPE.GROUP; displayOrder: number; productGroupId: UUID }
		>;
		isExtraProductConfigurationRequired: (params: { ticketProductId: UUID; ticketTypeId: UUID }) => boolean;
		addExtraProductToCartedTicket: (params: {
			ticketProductId: UUID;
			cartTicketId: UUID;
			initialValue: Partial<CartStoreCartedTicketsSlice.CartedTicketExtraProduct>;
		}) => void;
		updateExtraProductInCartedTicket: (params: {
			cartProductId: UUID;
			cartTicketId: UUID;
			update: Partial<CartStoreCartedTicketsSlice.CartedTicketExtraProduct>;
		}) => void;
		removeExtraProductFromCartedTicket: (params: { cartProductId: UUID; cartTicketId: UUID }) => void;
		getExtraProductStateById: (params: { ticketProductId: UUID; cartTicketId: UUID; cartProductId?: UUID }) => {
			quantityLimited: boolean;
			soldOut: boolean;
			productWithinTicketCount: number;
			allCartedProductsWithinTicketsCount: number;
		};
		getCartedExtraProductChipAmount: (params: { cartProductId: UUID; cartTicketId: UUID }) => number;
		getCartedExtraProductPrice: (params: { cartProductId: UUID | undefined; ticketProductId: UUID | undefined; cartTicketId: UUID }) => number;
		getCartedExtraProduct: (params: { cartProductId: UUID; cartTicketId: UUID }) => {
			ticket: CartStoreCartedTicketsSlice.CartedTicket;
			extraProduct: CartStoreCartedTicketsSlice.CartedTicketExtraProduct;
			extraProductConfig: CartStoreExtraProductsSlice.ExtraProductConfig;
		};
		setExtraProductConfigSheetProductInput: (params: { cartTicketId: UUID; ticketProductId: UUID; cartProductId?: UUID } | null) => void;
		setExtraProductConfigSheetVariantsInput: (params: { cartTicketId: UUID; productGroupId: UUID; cartProductId?: UUID } | null) => void;
	}

	export interface Store extends State, Actions {}

	export interface Selectors {}
}

const cartStoreExtraProductsSelectors = {} as const satisfies CartStoreExtraProductsSlice.Selectors;

interface RawExtraProductData extends FindAllExtraProductsFromTicketTypeResultDto {}

interface RawExtraProductOptions {
	eventId: UUID;
}

export function createExtraProductConfig(
	extraProduct: RawExtraProductData,
	options: RawExtraProductOptions,
): CartStoreExtraProductsSlice.ExtraProductConfig {
	/** construct extra product config base */
	const base = {
		eventId: options.eventId,
		ticketProductId: extraProduct.ticketProductId,
		name: extraProduct.name,
		description: extraProduct.description,
		price: extraProduct.price,
		displayOrder: extraProduct.displayOrder,
		optionalTextRegex: extraProduct.optionalTextRegex,
		gallery: extraProduct.gallery,
		productGroupId: extraProduct.productGroupId,
		productsAvailable: extraProduct.productsAvailable,
		productItems: extraProduct.productItems.map((productItem) => ({
			itemTypeId: productItem.itemTypeId,
			quantity: productItem.quantity,
			productItemId: productItem.productItemId,
		})),
		variants: extraProduct.variants.reduce<CartStoreExtraProductsSlice.ExtraProductConfigVariantCombo<number>>(
			(acc, combination) => ({ ...acc, [combination.variantId]: combination.valueId }),
			{},
		),
	} satisfies CartStoreExtraProductsSlice.ExtraProductConfig;

	const withHardOverrides = getExtraProductDataOverride(base);

	return withHardOverrides;
}

interface RawExtraProductGroupData extends FindAllProductGroupsFromTicketTypeResultDto {}

interface RawExtraProductGroupOptions {
	eventId: UUID;
}

export function createExtraProductGroupConfig(
	extraProductGroup: RawExtraProductGroupData,
	options: RawExtraProductGroupOptions,
): CartStoreExtraProductsSlice.ExtraProductGroupConfig {
	/** construct extra product group config base */
	const base = {
		eventId: options.eventId,
		productGroupId: extraProductGroup.productGroupId,
		name: extraProductGroup.name,
		description: extraProductGroup.description,
		gallery: extraProductGroup.gallery,
		quantityLimit: -1,
		variants: extraProductGroup.variants,
		displayOrder: 1,
	} satisfies CartStoreExtraProductsSlice.ExtraProductGroupConfig;

	const withHardOverrides = getTicketProductGroupOverride(base);

	return withHardOverrides;
}

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Carted Tickets
 * -----------------------------------------------------------------------------------------------*/
export const cartedTicketChipSchema = z.object({ cartChipId: z.string().uuid(), amount: z.number().int().min(0), productItemId: z.string().uuid() });

export const cartedTicketExtraProductSchema = z.object({
	cartProductId: z.string().uuid(),
	ticketProductId: z.string().uuid(),
	count: z.number().int().min(1),
	chips: z.array(cartedTicketChipSchema),
	optionalText: z.string().optional(),
	optionalTextShippingAddress: z
		.object({
			address: z.string().min(1).max(100),
			city: z.string().min(1).max(100),
			postalCode: z.string().min(1).max(100),
			country: z.nativeEnum(COUNTRY),
		})
		.optional(),
});

export const cartedTicketSchema = z.object({
	cartTicketId: z.string().uuid(),
	ticketTypeId: z.string().uuid(),
	holderName: z.string().min(0).max(100),
	chips: z.array(cartedTicketChipSchema),
	extraProducts: z.array(cartedTicketExtraProductSchema),
	userData: z.record(z.union([z.string(), z.number()])),
	reservation: z.object({
		seat: z.object({ reservationSeatId: z.string().uuid(), seatId: z.string().uuid() }).nullable(),
		crowd: z.object({ crowdId: z.string().uuid(), reservationCrowdId: z.string().uuid() }).nullable(),
	}),
});

export namespace CartStoreCartedTicketsSlice {
	export interface CartedTicket extends z.infer<typeof cartedTicketSchema> {}

	export interface CartedTicketExtraProduct extends z.infer<typeof cartedTicketExtraProductSchema> {}

	export type CartedTicketChip = z.infer<typeof cartedTicketSchema>['chips'][0];

	export interface ImportedTicket {
		ticketId: UUID;
		email: string;
		holderName: string;
		chips: Array<{ chipId: string }>;
	}

	export interface AddToCartInput {
		ticketTypeId: UUID;
		holderName: string;
		chips?: Array<Omit<CartedTicketChip, 'cartChipId'>>;
		reservation?: CartedTicket['reservation'];
	}

	export interface State {
		cartedTickets: Array<CartedTicket>;
		cartedTicketsFormErrors: Record<UUID, Partial<Record<FieldPath<CartedTicket>, string>>>;
		cartedTicketsTouchedFields: Record<UUID, Set<FieldPath<CartedTicket>>>;
		cartedTicketsDirtyFields: Record<UUID, Set<FieldPath<CartedTicket>>>;
	}

	export interface Actions {
		getCartedTicketHolderNameFallback: (params: { cartTicketId?: UUID }) => string;
		updateCartedTicketField: <TFieldName extends FieldPath<CartedTicket>>(params: {
			cartTicketId: UUID;
			field: TFieldName;
			value: FieldPathValue<CartedTicket, TFieldName>;
		}) => void;
		getCartedTicketSchemaForTicketType: (params: { ticketTypeId: UUID }) => z.ZodObject<any>;
		setCartedTicketFieldTouched: <TFieldName extends FieldPath<CartedTicket>>(params: { cartTicketId: UUID; field: TFieldName }) => void;
		validateCartedTicket: (params: { cartTicketId: UUID }) => boolean;
		validateCartedTicketsForm: () => boolean;
		resetCartedTicketsForm: () => void;
		getCartedTicketTotal: (params: { cartTicketId: UUID }) => {
			ticket: number;
			ticketOriginal: number;
			extraProducts: number;
			extraProductsOriginal: number;
			chips: number;
			extraProductChips: number;
			total: number;
			totalOriginal: number;
		};
		getCartedTicketsTotal: () => ReturnType<CartStore.Store['getCartedTicketTotal']>;
		addCartedTicket: (params: { input: AddToCartInput; trackingModule: TrackingModule | null }) => void;
		addCartedTicketOfType: (params: {
			quantity: number;
			ticketTypeId: UUID;
			input: Partial<AddToCartInput>;
			trackingModule: TrackingModule | null;
		}) => void;
		removeCartedTicketOfType: (params: { quantity: number; ticketTypeId: UUID; trackingModule: TrackingModule | null }) => void;
		removeCartedTickets: (params: { cartTicketIds: UUID[]; trackingModule: TrackingModule | null }) => Promise<void>;
		removeAllCartedTicketsByTicketType: (params: { ticketTypeIds: UUID[]; trackingModule: TrackingModule | null }) => void;
		getCartedTicket: (params: { cartTicketId: UUID }) => { ticket: CartedTicket; ticketType: CartStoreTicketTypesSlice.TicketTypeConfig };
		getCartedTicketsOfType: (params: { ticketTypeId: UUID }) => Array<CartedTicket>;
		getCartedTicketTypes: () => Array<CartStoreTicketTypesSlice.TicketTypeConfig>;
		importTicketToCart: (params: { ticketType: CartStoreTicketTypesSlice.TicketTypeConfig; ticket: ImportedTicket; topupAmount: number }) => void;
	}

	export interface Store extends State, Actions {}

	export interface Selectors {
		hasSomeImportedCartedTicket: (state: CartStore.Store) => boolean;
		listCartedTicketsWithExtraProducts: (state: CartStore.Store) => Array<CartedTicket>;
	}
}

const cartStoreCartedTicketsSelectors = {
	hasSomeImportedCartedTicket(state) {
		return state.cartedTickets.some((cartedTicket) => {
			const cartedTicketType = state.getTicketTypeById({ ticketTypeId: cartedTicket.ticketTypeId });
			if (cartedTicketType.__isImported) return true;
			return false;
		});
	},
	listCartedTicketsWithExtraProducts(state) {
		return state.cartedTickets.filter((cartedTicket) => {
			return state.hasTicketTypeExtraProducts({ ticketTypeId: cartedTicket.ticketTypeId });
		});
	},
} as const satisfies CartStoreCartedTicketsSlice.Selectors;

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Contact Details
 * -----------------------------------------------------------------------------------------------*/
const contactDetailsSchema = z.object({
	email: z.string().email().max(100),
	firstName: z.string().max(50).min(1),
	lastName: z.string().max(50).min(1),
	phone: z.string().max(25).nullish(),
	regNumber: z.string().max(25).nullish(),
	vatId: z.string().max(25).nullish(),
	legalName: z.string().max(100).nullish(),
	address: z.string().max(100).nullish(),
	city: z.string().max(100).nullish(),
	postalCode: z.string().max(20).nullish(),
	countryId: z.nativeEnum(COUNTRY).nullish(),
	languageId: z.nativeEnum(LANGUAGE),
});

export namespace CartStoreContactDetailsSlice {
	interface ContactDetails extends z.infer<typeof contactDetailsSchema> {}

	export interface State {
		contactDetails: ContactDetails;
		isContactDetailsSheetOpen: boolean;
	}

	export interface Actions {
		updateContactDetails: (values: ContactDetails) => void;
		setContactDetailsSheetOpen: (open: boolean) => void;
		canCloseContactDetailsSheet: () => boolean;
	}

	export interface Store extends State, Actions {}

	export interface Selectors {}
}

const cartStoreContactDetailsSelectors = {} as const satisfies CartStoreContactDetailsSlice.Selectors;

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Maximus
 * -----------------------------------------------------------------------------------------------*/
export namespace CartStoreMaximusSlice {
	export enum MAXIMUS_PRODUCT_TYPE {
		TICKET_GIFT_WRAP = 1,
		TICKET_PROCESSING_FEE = 2,
		PAYMENT_PROCESSING_FEE = 3,
		TREE_DONATION = 4,
		TICKET_GIFT_WRAP_DELIVERY = 5,
	}

	export interface MaximusProductConfig {
		maximusProductId: UUID;
		eventId: UUID;
		productTypeId: MAXIMUS_PRODUCT_TYPE;
		namePub: string;
		description: string;
		price: number;
		displayOrder: number;
		productsAvailable: number;
		extraData: object | null;
		company: { companyId: UUID; legalName: string };
		ticketTypes: Array<{ ticketTypeId: UUID; minQuantity: number; maxQuantity: number }>;
		paymentMethods: Array<{ paymentMethodId: PAYMENT_METHOD; minQuantity: number; maxQuantity: number }>;
	}

	export interface MaximusProductConfigForTicketType extends Omit<MaximusProductConfig, 'ticketTypes'> {
		minQuantity: number;
		maxQuantity: number;
	}

	export interface MaximusProductConfigForPaymentMethod extends Omit<MaximusProductConfig, 'paymentMethods'> {
		minQuantity: number;
		maxQuantity: number;
	}

	export interface MaximusCartedProduct {
		cartMaximusProductId: UUID;
		maximusProductId: UUID;
		count: number;
		cartTicketId: UUID | null;
		paymentMethodId: PAYMENT_METHOD | null;
		// TODO
		// cartedTicketIds?: Array<{ cartTicketId: UUID }>;
	}

	interface AddToCartInput {
		maximusProductId: UUID;
		count: number;
		cartTicketId: UUID | null;
		paymentMethodId: PAYMENT_METHOD | null;
	}

	export interface State {
		maximusProductsConfig: Array<MaximusProductConfig>;
		maximusCartedProducts: Array<MaximusCartedProduct>;
	}

	export interface Actions {
		loadMaximusProductsConfig: (params: {
			maximusProducts: Array<MaximusProductConfig>;
			duplicatesStrategy: 'ignore' | 'replace';
			forceOverwrite: boolean;
		}) => void;
		getCartedMaximusProductsForCartedTicket: (params: {
			cartTicketId: UUID;
		}) => Array<{ maximusProductConfig: MaximusProductConfig; maximusProduct: MaximusCartedProduct }>;
		getMaximusProductById: (params: { maximusProductId: UUID }) => MaximusProductConfig;
		getMaximusProductsForTicketType: (params: { ticketTypeId: UUID }) => Array<MaximusProductConfigForTicketType>;
		getMaximusProductStateById: (params: { maximusProductId: UUID }) => { soldOut: boolean };
		getMaximusProductsForPaymentMethod: (params: { paymentMethodId: PAYMENT_METHOD }) => Array<MaximusProductConfigForPaymentMethod>;
		addMaximusCartedProduct: (params: { input: AddToCartInput }) => void;
		removeMaximusCartedProduct: (params: { cartMaximusProductId: UUID }) => void;
		removeMaximusCartedProductByPaymentMethod: (params: { paymentMethodId: PAYMENT_METHOD }) => void;
		removeMaximusCartedProductByCartedTicket: (params: { cartTicketId: UUID }) => void;
		getCartedMaximusProductsTotal: (params: { cartTicketId: UUID | null }) => {
			fees: number;
			products: number;
			total: number;
			totalOriginal: number;
			feesOriginal: number;
			productsOriginal: number;
		};
	}

	export interface Store extends State, Actions {}

	export interface Selectors {}
}

interface RawMaximusProductsConfig extends GetExtraProductsFromEventProductResultDto {}
interface RawMaximusCompaniesConfig extends GetExtraProductsFromEventCompanyResultDto {}
interface RawMaximusProductsOptions {
	eventId: UUID;
}

export function createMaximusProductConfig(
	rawMaximusProductConfig: RawMaximusProductsConfig,
	rawMaximusCompaniesConfig: Array<RawMaximusCompaniesConfig>,
	options: RawMaximusProductsOptions,
): CartStoreMaximusSlice.MaximusProductConfig {
	/** find the company */
	const company = rawMaximusCompaniesConfig.find((c) => c.companyId === rawMaximusProductConfig.companyId);
	if (!isDefined(company)) {
		throw new InternalError(`Company with id ${rawMaximusProductConfig.companyId} not found`);
	}

	// TODO: handle extraData
	const extraData = rawMaximusProductConfig.extraData;

	return {
		// FIXME: API untyped
		paymentMethods: [],
		ticketTypes: [],
		...rawMaximusProductConfig,
		// FIXME: API returning productLimit
		productsAvailable: rawMaximusProductConfig.productLimit,
		maximusProductId: rawMaximusProductConfig.productId,
		eventId: options.eventId,
		company,
		extraData,
	};
}

const cartStoreMaximusSelectors = {} as const satisfies CartStoreMaximusSlice.Selectors;

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Global
 * TODO: maybe separate into multiple files (but fuck that for now)
 * -----------------------------------------------------------------------------------------------*/
const log = appLogger.extend('cartStore');

function loadConfigWithStrategy<TData extends object>(
	currentConfig: Array<TData>,
	newConfig: Array<TData>,
	options: { duplicatesStrategy: 'ignore' | 'replace'; forceOverwrite: boolean; identifier: (data: TData) => string },
): Array<TData> {
	/** set only the currently provided config if forceReload requested */
	if (options.forceOverwrite) return newConfig;
	/** check for duplicates */
	const hasDuplicates = newConfig.some((tt) => currentConfig.findIndex((ctt) => options.identifier(ctt) === options.identifier(tt)) !== -1);
	/** handle duplicates with `ignore` strategy */
	if (hasDuplicates && options.duplicatesStrategy === 'ignore') {
		log.warn('loadConfigWithStrategy', 'ignoring duplicate items');
		return [
			...currentConfig,
			/** ignore provided duplicates */
			...newConfig.filter((tt) => currentConfig.findIndex((ctt) => options.identifier(ctt) === options.identifier(tt)) === -1),
		];
	}
	/** handle duplicates with `replace` strategy */
	if (hasDuplicates && options.duplicatesStrategy === 'replace') {
		log.warn('loadConfigWithStrategy', 'replacing duplicate items');
		return [
			/** replace duplicates with provided */
			...currentConfig.filter((ctt) => newConfig.findIndex((tt) => options.identifier(ctt) === options.identifier(tt)) === -1),
			...newConfig,
		];
	}
	return [...currentConfig, ...newConfig];
}

export enum CART_VIEW {
	TICKETS = 'tickets',
	CART = 'cart',
	PRODUCTS = 'products',
	CHECKOUT = 'checkout',
}

export namespace CartStore {
	export interface CartConfig {
		eventId: UUID;
		currencyId: CURRENCY;
		eventTypeId: EVENT_TYPE;
		reqSourceId: REQ_SOURCE.WEB | REQ_SOURCE.WEB_DINO_LIVE | REQ_SOURCE.WEB_UNITED_TICKETS;
		isDemoCart: boolean;
		isReservationVenue: boolean;
		isEntryQueuePrevented: boolean;
		isEntryQueueActive: boolean;
		isTicketHolderNameRequired: boolean;
		isTicketEmptyChipsGenerated: boolean;
		saleInfo: { ticketsSet: boolean; saleFrom: Date | null; saleTo: Date | null; countdownDate: Date | null };
		importProviders: { nfctron: EventExtraData['nfctronImportProvider']; goout: EventExtraData['gooutImportProvider'] };
	}

	export interface InitParams {
		cartConfig: CartStoreConfigSlice.CartConfig;
		queryClient: QueryClient;
		sessionUserAccount: UserAccount | null;
		bankAccounts: Array<{ bankAccountId: UUID; currencyId: CURRENCY }>;
		paymentMethods: Array<{ paymentMethodId: PAYMENT_METHOD; displayOrder: number }>;
	}

	export interface Selectors
		extends CartStoreConfigSlice.Selectors,
			CartStoreTicketTypesSlice.Selectors,
			CartStorePaymentOptionsSlice.Selectors,
			CartStoreTicketTypeGroupsSlice.Selectors,
			CartStoreSeatingSlice.Selectors,
			CartStoreExtraProductsSlice.Selectors,
			CartStoreCartedTicketsSlice.Selectors,
			CartStoreContactDetailsSlice.Selectors,
			CartStoreMaximusSlice.Selectors {}

	export interface StoreBase
		extends CartStoreConfigSlice.Store,
			CartStorePaymentOptionsSlice.Store,
			CartStoreTicketTypesSlice.Store,
			CartStoreTicketTypeGroupsSlice.Store,
			CartStoreSeatingSlice.Store,
			CartStoreExtraProductsSlice.Store,
			CartStoreCartedTicketsSlice.Store,
			CartStoreContactDetailsSlice.Store,
			CartStoreMaximusSlice.Store {}

	export interface Store extends StoreBase, CartMultiViewSlice<CART_VIEW, StoreBase> {
		getCartTotal: () => {
			total: number;
			totalTickets: number;
			totalChips: number;
			totalExtraProducts: number;
			totalMaximusProducts: number;
			totalMaximusProcessingFees: number;
			/** original total */
			originalTotal: number;
			originalTotalTickets: number;
			originalTotalChips: number;
			originalTotalExtraProducts: number;
			originalTotalMaximusProducts: number;
			originalTotalMaximusProcessingFees: number;
		};
	}
}

export const cartStoreSelectors = {
	...cartStoreConfigSliceSelectors,
	...cartStorePaymentOptionsSelectors,
	...cartStoreTicketTypesSelectors,
	...cartStoreTicketTypeGroupsSelectors,
	...cartStoreSeatingSelectors,
	...cartStoreExtraProductsSelectors,
	...cartStoreCartedTicketsSelectors,
	...cartStoreContactDetailsSelectors,
	...cartStoreMaximusSelectors,
} as const satisfies CartStore.Selectors;

function createCartStore(initialState: CartStore.InitParams) {
	/** load payment options config helper */
	const getLoadPaymentOptionsConfig = (
		paymentMethods: Array<{ paymentMethodId: PAYMENT_METHOD; displayOrder: number }>,
		bankAccounts: Array<{ bankAccountId: UUID; currencyId: CURRENCY }>,
	) => {
		return {
			paymentOptionsConfig: paymentOptionsPresets
				.reduce<Array<CartStorePaymentOptionsSlice.PaymentOptionConfig>>((acc, preset) => {
					/** check if the payment option is supported by the payment methods */
					const eventPaymentMethod = paymentMethods.find((pm) => pm.paymentMethodId === preset.paymentMethodId);
					if (!isDefined(eventPaymentMethod)) return acc;

					switch (preset.paymentOptionId) {
						/** handle bank transfer payment options (add accounts) */
						case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_CZK:
						case CartStorePaymentOptionsSlice.PAYMENT_OPTION_ID.BANK_TRANSFER_EUR: {
							const bankAccount = bankAccounts.find((ba) => preset.currencyIdSupported.some((ci) => ci === ba.currencyId));
							if (!isDefined(bankAccount)) return acc;
							return [
								...acc,
								{
									...preset,
									displayOrder: eventPaymentMethod.displayOrder,
									bankAccount,
								} satisfies CartStorePaymentOptionsSlice.PaymentOptionConfigBankTransfer,
							];
						}
						default: {
							return [
								...acc,
								{ ...preset, displayOrder: eventPaymentMethod.displayOrder } satisfies CartStorePaymentOptionsSlice.PaymentOptionConfigBasic,
							];
						}
					}
				}, [])
				.sort(sortBy('displayOrder', { direction: 'asc' })),
		};
	};

	return create<CartStore.Store>()(
		devtools(
			(set, get, api) => {
				/** load payment options config */
				const { paymentOptionsConfig } = getLoadPaymentOptionsConfig(initialState.paymentMethods, initialState.bankAccounts);

				/** prepare ticket to cart helper */
				function prepareTicketToCart(rawTicketInput: CartStoreCartedTicketsSlice.AddToCartInput) {
					const snapshot = get();
					const ticketType = snapshot.getTicketTypeById({ ticketTypeId: rawTicketInput.ticketTypeId });
					log.debug(`Preparing ticket type "${ticketType.namePub}" to the cart`);
					/** validate if the ticket can be added */
					const ticketTypeIs = snapshot.getTicketTypeStateById({ ticketTypeId: rawTicketInput.ticketTypeId });
					if (ticketTypeIs.soldOut) {
						throw new InvalidStateError(`Could not cart ticket type "${rawTicketInput.ticketTypeId}" because it is sold out.`);
					}
					if (ticketTypeIs.orderLimited) {
						throw new InvalidStateError(`Could not cart ticket type "${rawTicketInput.ticketTypeId}" because it would exceed order limit.`);
					}

					/** generate unique uuid for the new ticket */
					const cartTicketId = uuidv4();
					/** generate chips */
					const generatedChips = ticketType.chips.map(({ productItemId }) => ({ productItemId, amount: 0 }));
					const chips = rawTicketInput.chips ?? generatedChips;

					/** get default holderName */
					const holderNameFallback = snapshot.getCartedTicketHolderNameFallback({ cartTicketId: undefined });

					const cartedTicket = {
						cartTicketId,
						ticketTypeId: ticketType.ticketTypeId,
						holderName: rawTicketInput.holderName || holderNameFallback,
						extraProducts: [],
						/** generate cartChipIds */
						chips: chips.map((chip) => ({ cartChipId: uuidv4(), ...chip })),
						/** add user data from fields schema */
						userData:
							ticketType.userData?.fields.reduce((acc, field) => {
								return {
									...acc,
									/** add field with default value */
									[field.fieldId]: field.defaultValue ?? (field.type === 'number' ? 0 : ''),
								};
							}, {}) ?? {},
						/** seating reservation */
						reservation: rawTicketInput.reservation ?? { crowd: null, seat: null },
					} satisfies CartStoreCartedTicketsSlice.CartedTicket;

					return { cartedTicket, ticketType };
				}

				return {
					cartConfig: initialState.cartConfig,
					queryClient: initialState.queryClient,
					/** cart view slice */
					...createCartMultiViewSlice<CART_VIEW, CartStore.StoreBase>(CART_VIEW.TICKETS, {
						restoreScroll: true,
						onCartViewChange(newCartViewId) {
							/** fallback to previous enabled view if not allowed */
							const newStepState = get().getCartViewEvaluated({ view: newCartViewId as CART_VIEW });
							if (!newStepState.isEnabled) {
								const prevView = get().getPrevCartView({ prop: 'isEnabled' });
								set({ currentCartView: prevView }, undefined, 'onCartViewChange');
								return;
							}

							/** custom callbacks upon specific views */
							switch (newCartViewId) {
								case CART_VIEW.CHECKOUT: {
									/** auto-open contact details sheet if no `contactDetails` set */
									const hasContactDetails = !isEmpty(get().contactDetails.email);
									set({ isContactDetailsSheetOpen: !hasContactDetails }, undefined, 'onCartViewChange');
									/** auto-select first payment method if not set */
									if (!isDefined(get().activePaymentOptionId)) {
										get().setActivePaymentOptionId({ paymentOptionId: paymentOptionsConfig[0]?.paymentOptionId ?? null });
									}
									break;
								}
								default:
									break;
							}

							/** scroll to the <main> content */
							silenceError(() => {
								const mainElement = document.querySelector('main');
								/** account for 75px of overflowing header when scrolling */
								const headerOffset = 75;
								const mainElementPosition = mainElement?.getBoundingClientRect().top;
								const mainElementOffset = mainElementPosition ? mainElementPosition + window.scrollY - headerOffset : 0;

								/** scroll only if more than half of the screen height is not visible */
								const windowHeight = window.innerHeight;
								const visibleRatio = mainElementPosition ? mainElementPosition / windowHeight : 0;

								if (visibleRatio < -0.5 || visibleRatio > 0.5) {
									window.scrollTo({ top: mainElementOffset, behavior: 'instant' });
								}
							});
						},
						cartViewsConfig: [
							{
								viewId: CART_VIEW.TICKETS,
								getIsVisible(state) {
									return !state.autoCartTicketType?.isOnlyOne;
								},
								getIsEnabled: true,
								getIsSelectable(state) {
									return !state.autoCartTicketType?.isOnlyOne;
								},
							},
							{
								viewId: CART_VIEW.CART,
								getIsVisible: true,
								getIsEnabled(state) {
									/** cart view enabled only with carted tickets or autoCartPackage is available */
									return isDefined(state.autoCartTicketType) || state.cartedTickets.length > 0;
								},
								getIsSelectable(state) {
									/** selectable only from following view (products/checkout) */
									const validViews = [CART_VIEW.PRODUCTS, CART_VIEW.CHECKOUT];
									return validViews.includes(state.currentCartView);
								},
							},
							{
								viewId: CART_VIEW.PRODUCTS,
								getIsVisible(state) {
									/** visible only when any tickets allows to cart extra products */
									const hasTicketsWithExtraProducts = state
										.getCartedTicketTypes()
										.some((ticket) => state.hasTicketTypeExtraProducts({ ticketTypeId: ticket.ticketTypeId }));
									return hasTicketsWithExtraProducts;
								},
								getIsEnabled(state) {
									/** check if there are any tickets with extra products */
									const hasTicketsWithExtraProducts = state
										.getCartedTicketTypes()
										.some((ticket) => state.hasTicketTypeExtraProducts({ ticketTypeId: ticket.ticketTypeId }));

									/** check if current view is either cart or products */
									const validViews = [CART_VIEW.CART, CART_VIEW.PRODUCTS, CART_VIEW.CHECKOUT];
									const isOnValidView = validViews.includes(state.currentCartView);

									/** enable only when both conditions are met */
									return isOnValidView && hasTicketsWithExtraProducts;
								},
								getIsSelectable(state) {
									/** selectable only from following view (checkout) */
									const validViews = [CART_VIEW.CHECKOUT];
									return validViews.includes(state.currentCartView);
								},
							},
							{
								viewId: CART_VIEW.CHECKOUT,
								getIsVisible: true,
								getIsEnabled(state) {
									/** check if there are any carted tickets and if they are valid */
									const isCartedTicketsFormValid = objectEntries(state.cartedTicketsFormErrors).every(([, errors]) => {
										return objectKeys(errors).length === 0;
									});
									/** enabled only when can proceed to checkout */
									const canProceedToCheckout = state.cartedTickets.length > 0 && isCartedTicketsFormValid;
									if (!canProceedToCheckout) return false;

									/** check if there are any tickets with extra products */
									const hasTicketsWithExtraProducts = state
										.getCartedTicketTypes()
										.some((ticket) => state.hasTicketTypeExtraProducts({ ticketTypeId: ticket.ticketTypeId }));

									/** with extra products - enabled only when on products view (previous) or on self (current) and has extra products config loaded */
									if (hasTicketsWithExtraProducts) {
										const hasExtraProductsConfigLoaded = state.extraProductsConfig.length > 0;
										const validViews = [CART_VIEW.PRODUCTS, CART_VIEW.CHECKOUT];
										return hasExtraProductsConfigLoaded && validViews.includes(state.currentCartView);
									}
									/** without extra products - enabled only when on cart view (previous) or on self (current) */
									const validViews = [CART_VIEW.CART, CART_VIEW.CHECKOUT];
									return validViews.includes(state.currentCartView);
								},
								getIsSelectable: false,
							},
						],
					})(set, get, api),
					paymentOptionsConfig,
					/** set first method as default */
					activePaymentOptionId: null,
					loadPaymentOptionsConfig({ paymentOptions }) {
						return set(getLoadPaymentOptionsConfig(paymentOptions, initialState.bankAccounts), undefined, 'loadPaymentOptionsConfig');
					},
					setActivePaymentOptionId({ paymentOptionId }) {
						const listedPaymentOptions = cartStoreSelectors.listPaymentOptions(get());
						const newPaymentOption = listedPaymentOptions.find((pm) => pm.paymentOptionId === paymentOptionId);

						/** check if the new payment option is allowed */
						if (!isDefined(newPaymentOption) || newPaymentOption.disabled) {
							log.warn('setActivePaymentOptionId', 'payment option not allowed', { paymentOptionId });
							return;
						}

						set(
							(snapshot) => {
								/** remove any previously added maximus products (with the current payment method) */
								const oldPaymentOption = snapshot.getActivePaymentOption();
								if (isDefined(oldPaymentOption)) {
									get().removeMaximusCartedProductByPaymentMethod({ paymentMethodId: oldPaymentOption.paymentMethodId });
								}

								/** add maximus product to carted tickets if tied with selected payment method (that should be auto added) */
								get()
									.getMaximusProductsForPaymentMethod({ paymentMethodId: newPaymentOption.paymentMethodId })
									.filter((mp) => mp.minQuantity > 0)
									.forEach((mp) => {
										log.debug('Auto-adding maximus product due to minQuantity with selected payment method', {
											maximusProductId: mp.maximusProductId,
											count: mp.minQuantity,
										});
										snapshot.addMaximusCartedProduct({
											input: {
												cartTicketId: null,
												paymentMethodId: newPaymentOption.paymentMethodId,
												maximusProductId: mp.maximusProductId,
												count: mp.minQuantity,
											},
										});
									});

								return { activePaymentOptionId: paymentOptionId };
							},
							undefined,
							'setActivePaymentOptionId',
						);
					},
					getActivePaymentOption() {
						const snapshot = get();
						return snapshot.paymentOptionsConfig.find((pm) => pm.paymentOptionId === snapshot.activePaymentOptionId) ?? null;
					},
					/** contact details */
					contactDetails: {
						firstName: initialState.sessionUserAccount?.account.firstName ?? '',
						lastName: initialState.sessionUserAccount?.account.lastName ?? '',
						email: initialState.sessionUserAccount?.account.email ?? '',
						phone: initialState.sessionUserAccount?.account.phone ?? '',
						legalName: initialState.sessionUserAccount?.account.billingInfo.legalName,
						address: initialState.sessionUserAccount?.account.billingInfo.address,
						city: initialState.sessionUserAccount?.account.billingInfo.city,
						postalCode: initialState.sessionUserAccount?.account.billingInfo.postalCode,
						countryId: initialState.sessionUserAccount?.account.billingInfo.countryId ?? COUNTRY.CZE,
						vatId: initialState.sessionUserAccount?.account.billingInfo.vatId,
						regNumber: initialState.sessionUserAccount?.account.billingInfo.regNumber,
						languageId: initialState.sessionUserAccount?.account.languageId ?? LANGUAGE.CZECH,
					},
					isContactDetailsSheetOpen: false,
					updateContactDetails(values) {
						return set({ contactDetails: values }, undefined, 'updateContactDetails');
					},
					setContactDetailsSheetOpen(open) {
						return set({ isContactDetailsSheetOpen: open }, undefined, 'setContactDetailsSheetOpen');
					},
					canCloseContactDetailsSheet() {
						const snapshot = get();
						return !isEmpty(snapshot.contactDetails.email);
					},
					/** ticket types */
					ticketTypesConfig: [],
					ticketTypesListedGroupedOpen: {},
					autoCartTicketType: null,
					loadTicketTypesConfig({ ticketTypes, forceOverwrite, duplicatesStrategy, shouldAutoCart }) {
						const snapshot = get();
						const currentTicketTypesConfig = snapshot.ticketTypesConfig;

						/** sort and process ticket types config with auto-append logic */
						const ticketTypesConfig = loadConfigWithStrategy<CartStoreTicketTypesSlice.TicketTypeConfig>(currentTicketTypesConfig, ticketTypes, {
							duplicatesStrategy,
							forceOverwrite,
							identifier: (tt) => tt.ticketTypeId,
						})
							.sort(sortBy('displayOrder', { direction: 'asc' }))
							.map((ticketType, index, array) => {
								/** check if ticket type is part of auto-append in any other ticket type */
								const isExistingWithinAutoAppend = array.some((tt) =>
									tt.extraData.autoAppendTicketTypes.some((autoAppend) => autoAppend.ticketTypeId === ticketType.ticketTypeId),
								);

								if (isExistingWithinAutoAppend) {
									ticketType.__isListable = false;
								}

								return ticketType;
							});

						/** initialize groups open state - first group is open by default */
						const ticketTypesListedGroupedOpen = ticketTypesConfig.reduce<CartStore.Store['ticketTypesListedGroupedOpen']>(
							(acc, group) => ({ ...acc, [group.ticketTypeGroup?.ticketTypeGroupId ?? 'default']: true }),
							{},
						);

						/** auto-cart logic - only when initializing and there's exactly one ticket type */
						const autoCartTicketType =
							/** its the only and only one */
							currentTicketTypesConfig.length === 0 &&
							ticketTypesConfig.length === 1 &&
							isDefined(ticketTypesConfig[0]) &&
							/** and is not seating (non-reservable) */
							!ticketTypesConfig[0].isReservable
								? { config: ticketTypesConfig[0], isOnlyOne: ticketTypesConfig[0].ticketLimitOrder === 1 }
								: null;

						if (isDefined(autoCartTicketType)) {
							if (shouldAutoCart && snapshot.cartedTickets.length === 0 && autoCartTicketType.isOnlyOne) {
								/** first we need to lad the ticket types (otherwise the addCartedTicketOfType will fail becuase it will not have the ticket type at the time) */
								set({ ticketTypesConfig, ticketTypesListedGroupedOpen });

								/** add ticket to cart, if any available */
								get().addCartedTicketOfType({
									quantity: 1,
									ticketTypeId: autoCartTicketType.config.ticketTypeId,
									input: { holderName: '' },
									/** TODO: pass somehow trackingModule reference here? */
									trackingModule: null,
								});
							}

							/** with auto-cart logic – after loading ticket types config, we can cart the auto-cart ticket type */
							set({
								autoCartTicketType: get().autoCartTicketType ?? autoCartTicketType,
								/** set cart view to cart if there's only one auto-cart ticket type (package) */
								currentCartView: autoCartTicketType.isOnlyOne ? CART_VIEW.CART : CART_VIEW.TICKETS,
								ticketTypesConfig,
								ticketTypesListedGroupedOpen,
							});
						} else {
							/** without auto-cart logic - just load the ticket types config */
							set({ ticketTypesConfig, ticketTypesListedGroupedOpen });
						}
					},
					removeTicketTypesFromConfig({ ticketTypeIds, trackingModule }) {
						const snapshot = get();

						const cartedTicketsToRemove = snapshot.cartedTickets.filter((t) => ticketTypeIds.includes(t.ticketTypeId)).map((t) => t.cartTicketId);
						get().removeCartedTickets({ cartTicketIds: cartedTicketsToRemove, trackingModule });

						const ticketTypesConfig = snapshot.ticketTypesConfig.filter((tt) => !ticketTypeIds.includes(tt.ticketTypeId));
						get().loadTicketTypesConfig({
							ticketTypes: ticketTypesConfig,
							duplicatesStrategy: 'ignore',
							forceOverwrite: true,
							shouldAutoCart: false,
						});
					},
					insertTicketTypeToConfig({ ticketType, duplicatesStrategy }) {
						get().loadTicketTypesConfig({ ticketTypes: ticketType, duplicatesStrategy, forceOverwrite: false, shouldAutoCart: false });
					},
					toggleTicketTypeGroup({ ticketTypeGroupId, changeTo, autoCloseOthers }) {
						return set(
							(snapshot) => {
								const ticketTypesListedGroupedOpen = { ...snapshot.ticketTypesListedGroupedOpen };

								if (autoCloseOthers) {
									/** close all groups first if autoClose is enabled */
									Object.keys(ticketTypesListedGroupedOpen).forEach((key) => {
										ticketTypesListedGroupedOpen[key] = false;
									});
								}

								ticketTypesListedGroupedOpen[ticketTypeGroupId] = changeTo;
								return { ticketTypesListedGroupedOpen };
							},
							undefined,
							'toggleTicketTypeGroup',
						);
					},
					getTicketTypeById({ ticketTypeId }) {
						const snapshot = get();
						return valOrThrow(
							snapshot.ticketTypesConfig.find((tt) => tt.ticketTypeId === ticketTypeId),
							`ticket type id = "${ticketTypeId}" not found`,
						);
					},
					hasTicketTypeExtraProducts({ ticketTypeId, ticketType }) {
						const snapshot = get();
						const ticketTypeConfig = isDefined(ticketTypeId)
							? snapshot.getTicketTypeById({ ticketTypeId })
							: valOrThrow(ticketType, 'ticketType is required');
						return ticketTypeConfig.extraProducts.length > 0;
					},
					hasTicketTypeChips({ ticketTypeId, ticketType }) {
						const snapshot = get();
						const ticketTypeConfig = isDefined(ticketTypeId)
							? snapshot.getTicketTypeById({ ticketTypeId })
							: valOrThrow(ticketType, 'ticketType is required');
						return ticketTypeConfig.chips.length > 0;
					},
					getTicketTypeStateById({ ticketTypeId }) {
						const snapshot = get();
						const ticketType = snapshot.getTicketTypeById({ ticketTypeId });
						const cartTickets = snapshot.cartedTickets.filter((cartTicket) => cartTicket.ticketTypeId === ticketTypeId);

						const isOrderLimited = ticketType.ticketLimitOrder === -1 ? false : ticketType.ticketLimitOrder - cartTickets.length <= 0;
						const isSoldOut = ticketType.ticketsAvailable === -1 ? false : ticketType.ticketsAvailable - cartTickets.length <= 0;

						return { orderLimited: isOrderLimited, soldOut: isSoldOut };
					},
					/** tickets */
					cartedTickets: [],
					cartedTicketsTouchedFields: {},
					cartedTicketsDirtyFields: {},
					cartedTicketsFormErrors: {},
					getCartedTicketSchemaForTicketType({ ticketTypeId }) {
						const ticketType = get().getTicketTypeById({ ticketTypeId });
						return cartedTicketSchema.extend({
							/** make holderName optional if the ticket type allows so */
							holderName:
								ticketType.isHolderRequired &&
								/** make imported ticketTypes also optional since it may not have been created with it (or the event settings had changed) */
								!ticketType.__isImported
									? cartedTicketSchema.shape.holderName.min(1, 't:holderName.requiredError')
									: cartedTicketSchema.shape.holderName.min(0).optional(),
							/** validate userData fields per ticket type schema */
							userData: isDefined(ticketType.userData)
								? z.object(
										ticketType.userData.fields.reduce((acc, field) => {
											let fieldSchema;
											if (field.type === 'number') {
												fieldSchema = z.number();
												if (isDefined(field.required)) fieldSchema = fieldSchema.min(1, field.requiredError ?? 't:userData.number.requiredError');
												if (isDefined(field.min))
													fieldSchema = fieldSchema.min(field.min, field.minError ?? `t:userData.number.minError(${field.min})`);
												if (isDefined(field.max))
													fieldSchema = fieldSchema.max(field.max, field.maxError ?? `t:userData.number.maxError(${field.max})`);
											}
											if (field.type === 'text') {
												fieldSchema = z.string();
												if (field.required) fieldSchema = fieldSchema.min(1, field.requiredError ?? 't:userData.text.requiredError');
												if (isDefined(field.minLength))
													fieldSchema = fieldSchema.min(
														field.minLength,
														field.minLengthError ?? `t:userData.text.minLengthError(${field.minLength})`,
													);
												if (isDefined(field.maxLength))
													fieldSchema = fieldSchema.max(
														field.maxLength,
														field.maxLengthError ?? `t:userData.text.maxLengthError(${field.maxLength})`,
													);
												if (isDefined(field.pattern)) {
													try {
														fieldSchema = fieldSchema.regex(new RegExp(field.pattern), field.patternError ?? 't:userData.text.patternError');
													} catch (e) {
														log.warn('getCartedTicketSchemaForTicketType', 'invalid regex pattern', { fieldId: field.fieldId });
													}
												}
											}
											return { ...acc, [field.fieldId]: fieldSchema };
										}, {}),
									)
								: z.record(z.union([z.string(), z.number()])),
							/** validate chips per ticket type min/max charges */
							chips: z.array(
								cartedTicketChipSchema.extend({
									/** handle imported ticket types with chipId (non-UUID) as productItemId and cartChipId */
									cartChipId: ticketType.__isImported ? z.string() : cartedTicketChipSchema.shape.cartChipId,
									productItemId: ticketType.__isImported ? z.string() : cartedTicketChipSchema.shape.productItemId,
									amount: z.union([
										/** credit allowed at exactly 0 (empty chip) */
										z.number().int().min(0).max(0),
										/** but with limits ranging from min to max */
										z
											.number()
											.int()
											.min(ticketType.creditMinimum, `t:chips.creditMinimum.minError(${ticketType.creditMinimum})`)
											.max(ticketType.creditLimit, `t:chips.creditLimit.maxError(${ticketType.creditLimit})`),
									]),
								}),
							),
						});
					},
					updateCartedTicketField(params) {
						return set(
							(state) => {
								/** update the ticket */
								const updatedCartedTickets = state.cartedTickets.map((cartedTicket) => {
									if (cartedTicket.cartTicketId === params.cartTicketId) {
										return setObject(cartedTicket, params.field, params.value);
									}
									return cartedTicket;
								}) satisfies CartStore.Store['cartedTickets'];

								/** update dirty fields */
								const updatedDirtyFieldsSet = new Set(state.cartedTicketsDirtyFields[params.cartTicketId]);
								/** add the current field as dirty */
								updatedDirtyFieldsSet.add(params.field);
								/** make all field parts dirty if nested path (e.g., make chips dirty if touching chips.0.value) */
								if (params.field.includes('.')) {
									const fieldParts = params.field.split('.');
									fieldParts.forEach((_, index) => {
										updatedDirtyFieldsSet.add(fieldParts.slice(0, index + 1).join('.') as any);
									});
								}

								/** validate the updated ticket */
								const ticket = updatedCartedTickets.find((t) => t.cartTicketId === params.cartTicketId);
								const cartedTicketsFormErrors: CartStore.Store['cartedTicketsFormErrors'][string] = {};
								const parseRes = state.getCartedTicketSchemaForTicketType({ ticketTypeId: ticket.ticketTypeId }).safeParse(ticket);
								if (!parseRes.success && parseRes.error instanceof z.ZodError) {
									parseRes.error.errors.forEach((err) => {
										const path = err.path.join('.') as FieldPath<CartStoreCartedTicketsSlice.CartedTicket>;
										cartedTicketsFormErrors[path] = err.message;
									});
								}

								return {
									cartedTickets: updatedCartedTickets,
									cartedTicketsFormErrors: { ...state.cartedTicketsFormErrors, [params.cartTicketId]: cartedTicketsFormErrors },
									cartedTicketsDirtyFields: { ...state.cartedTicketsDirtyFields, [params.cartTicketId]: updatedDirtyFieldsSet },
								};
							},
							undefined,
							'updateCartedTicketField',
						);
					},
					setCartedTicketFieldTouched(params) {
						return set(
							(state) => {
								/** update touched fields */
								const updatedTouchedFieldsSet = new Set(state.cartedTicketsTouchedFields[params.cartTicketId]);
								/** add the current field as touched */
								updatedTouchedFieldsSet.add(params.field);
								/** make all field parts touched if nested path (e.g., touch chips if touching chips.0.value) */
								if (params.field.includes('.')) {
									const fieldParts = params.field.split('.');
									fieldParts.forEach((_, index) => {
										updatedTouchedFieldsSet.add(fieldParts.slice(0, index + 1).join('.') as any);
									});
								}

								/** validate the touched fields */
								const ticket = state.cartedTickets.find((t) => t.cartTicketId === params.cartTicketId);
								const cartedTicketsFormErrors: CartStore.Store['cartedTicketsFormErrors'][string] = {};
								if (isDefined(ticket)) {
									const parseRes = state.getCartedTicketSchemaForTicketType({ ticketTypeId: ticket.ticketTypeId }).safeParse(ticket);
									if (!parseRes.success && parseRes.error instanceof z.ZodError) {
										/** but add errors to only touched fields (not all) */
										parseRes.error.errors.forEach((err) => {
											const path = err.path.join('.') as FieldPath<CartStoreCartedTicketsSlice.CartedTicket>;
											if (updatedTouchedFieldsSet.has(path)) {
												cartedTicketsFormErrors[path] = err.message;
											}
										});
									}
								}

								return {
									cartedTicketsFormErrors: { ...state.cartedTicketsFormErrors, [params.cartTicketId]: cartedTicketsFormErrors },
									cartedTicketsTouchedFields: { ...state.cartedTicketsTouchedFields, [params.cartTicketId]: updatedTouchedFieldsSet },
								};
							},
							undefined,
							'setCartedTicketFieldTouched',
						);
					},
					resetCartedTicketsForm() {
						return set(
							() => {
								/** reset all form errors */
								const cartedTicketsFormErrors: CartStore.Store['cartedTicketsFormErrors'] = {};
								/** reset all touched fields */
								const cartedTicketsTouchedFields: CartStore.Store['cartedTicketsTouchedFields'] = {};
								/** reset all dirty fields */
								const cartedTicketsDirtyFields: CartStore.Store['cartedTicketsDirtyFields'] = {};
								return { cartedTicketsFormErrors, cartedTicketsTouchedFields, cartedTicketsDirtyFields };
							},
							undefined,
							'resetCartedTicketsForm',
						);
					},
					validateCartedTicket(params) {
						const snapshot = get();
						const ticket = snapshot.cartedTickets.find((t) => t.cartTicketId === params.cartTicketId);
						const cartedTicketsFormErrors: CartStore.Store['cartedTicketsFormErrors'][string] = {};
						const parseRes = snapshot.getCartedTicketSchemaForTicketType({ ticketTypeId: ticket!.ticketTypeId }).safeParse(ticket);
						if (!parseRes.success && parseRes.error instanceof z.ZodError) {
							parseRes.error.errors.forEach((err) => {
								const path = err.path.join('.') as FieldPath<CartStoreCartedTicketsSlice.CartedTicket>;
								cartedTicketsFormErrors[path] = err.message;
							});
						}
						set({ cartedTicketsFormErrors: { ...snapshot.cartedTicketsFormErrors, [params.cartTicketId]: cartedTicketsFormErrors } });
						return parseRes.success;
					},
					validateCartedTicketsForm() {
						const snapshot = get();
						let valid = true;
						/** iterate over all tickets (to validate them all, not just the first invalid one) */
						snapshot.cartedTickets.forEach((ticket) => {
							if (!snapshot.validateCartedTicket({ cartTicketId: ticket.cartTicketId })) {
								valid = false;
							}
						});

						return valid;
					},
					addCartedTicket({ input, trackingModule }) {
						const { cartedTicket, ticketType } = prepareTicketToCart(input);

						/** get other ticket types to auto add to cart as well */
						const autoAppendTickets =
							ticketType.extraData?.autoAppendTicketTypes?.flatMap((tt) => {
								return arrayOf(tt.quantity).map(() => {
									return prepareTicketToCart({ ticketTypeId: tt.ticketTypeId, holderName: input.holderName }).cartedTicket;
								});
							}) ?? [];

						/** account in add to cart quantity config */
						const addToCartQtyTickets =
							isDefined(ticketType.extraData?.addToCartQty) && ticketType.extraData?.addToCartQty > 1
								? arrayOf(ticketType.extraData?.addToCartQty - 1).map(() => prepareTicketToCart(input).cartedTicket)
								: [];

						/** append to the cart form */
						log.debug(`Added a new "${ticketType.namePub}" ticket with id "${cartedTicket.cartTicketId}" to cart`);
						if (autoAppendTickets.length > 0) {
							log.debug(`Auto-appending tickets with ids: ${autoAppendTickets.map((t) => t.cartTicketId).join(', ')}`);
						}
						if (addToCartQtyTickets.length > 0) {
							log.debug(`Adding ${addToCartQtyTickets.length} more tickets to cart`);
						}

						/** ticket type restricted payment methods */
						const activePaymentOptionId = calcValue(() => {
							const allowedPaymentMethods = ticketType.extraData.promotionCampaign?.allowedPaymentMethods ?? [];
							const currentActivePaymentOption = get().getActivePaymentOption();
							const currentActivePaymentMethod = currentActivePaymentOption?.paymentMethodId;
							/** keep current if no restriction is present */
							if (allowedPaymentMethods.length === 0) return currentActivePaymentOption?.paymentOptionId;
							/** keep current one if enabled */
							if (isDefined(currentActivePaymentMethod) && allowedPaymentMethods.includes(currentActivePaymentMethod)) {
								return currentActivePaymentOption?.paymentOptionId;
							}
							/** use the first allowed method if defined */
							const allowedPaymentOptions = get().paymentOptionsConfig.filter((pm) => allowedPaymentMethods.includes(pm.paymentMethodId));
							return allowedPaymentOptions[0]?.paymentOptionId ?? null;
						});

						/** all tickets to be added to the cart */
						const toAddCartedTickets = [cartedTicket, ...autoAppendTickets, ...addToCartQtyTickets];

						set({ activePaymentOptionId, cartedTickets: [...get().cartedTickets, ...toAddCartedTickets] }, undefined, 'addCartedTicket');

						/** add maximus carted products (that should be auto added) */
						toAddCartedTickets.forEach((ticket) => {
							get()
								.getMaximusProductsForTicketType({ ticketTypeId: ticket.ticketTypeId })
								.filter((mp) => mp.minQuantity > 0)
								.forEach((mp) => {
									log.debug('Auto-adding maximus product due to minQuantity of added ticket type', {
										maximusProductId: mp.maximusProductId,
										count: mp.minQuantity,
									});
									get().addMaximusCartedProduct({
										input: { maximusProductId: mp.maximusProductId, count: mp.minQuantity, cartTicketId: ticket.cartTicketId, paymentMethodId: null },
									});
								});
						});

						/** handle add to cart event tracking (if provided) */
						if (isDefined(trackingModule)) {
							const cartConfig = get().cartConfig;
							trackingModule.safeTracking(() =>
								trackingModule.addToCart(
									{
										tickets: [
											{
												cartTicketId: cartedTicket.cartTicketId,
												ticketType,
												chips: cartedTicket.chips,
												extraProducts: [],
												reservation: cartedTicket.reservation,
												holderName: cartedTicket.holderName,
												userData: cartedTicket.userData,
												trackingContext: cartConfig.eventContext,
											},
										],
										value: ticketType.price,
										currencyId: cartConfig.currencyId,
									},
									{ eventId: cartConfig.eventId },
								),
							);
						}

						return cartedTicket.cartTicketId;
					},
					getCartedTicket({ cartTicketId }) {
						const ticket = get().cartedTickets.find((t) => t.cartTicketId === cartTicketId);
						if (!isDefined(ticket)) {
							throw new InvalidStateError(`Could not find ticket with id "${cartTicketId}" in the cart`);
						}
						const ticketType = get().getTicketTypeById({ ticketTypeId: ticket.ticketTypeId });
						return { ticket, ticketType };
					},
					importTicketToCart(params) {
						let leftAmount = params.topupAmount;
						return set((state) => {
							const alreadyImportedTicketType = silenceError(() => state.getTicketTypeById({ ticketTypeId: params.ticketType.ticketTypeId }), null);
							const updatedTicketTypesConfig = !isDefined(alreadyImportedTicketType)
								? [...state.ticketTypesConfig, params.ticketType]
								: state.ticketTypesConfig.map((tt) => {
										if (tt.ticketTypeId === alreadyImportedTicketType!.ticketTypeId) {
											return params.ticketType;
										}
										return tt;
									});
							const ticketType = valOrThrow(
								updatedTicketTypesConfig.find((tt) => tt.ticketTypeId === params.ticketType.ticketTypeId),
								'this should not happen',
							);

							/** generate chips */
							const importedGeneratedChips = params.ticket.chips.map((chip) => {
								const defaultAmount = ticketType.creditMinimum ?? 0;
								const amount = leftAmount > 0 ? Math.min(leftAmount, ticketType.creditLimit) : defaultAmount;
								leftAmount -= amount;
								return {
									cartChipId: chip.chipId,
									amount,
									/** NOTE: this is intentional */
									productItemId: chip.chipId,
								};
							});

							const alreadyCartedTicket = state.cartedTickets.find((t) => t.cartTicketId === params.ticket.ticketId);
							if (isDefined(alreadyCartedTicket)) {
								return {
									ticketTypesConfig: updatedTicketTypesConfig,
									cartedTickets: state.cartedTickets.map((ct) => {
										if (ct.cartTicketId === params.ticket.ticketId) {
											return { ...ct, ticketTypeId: ticketType.ticketTypeId, holderName: params.ticket.holderName, chips: importedGeneratedChips };
										}
										return ct;
									}),
								};
							}

							const importedCartedTicket = {
								cartTicketId: params.ticket.ticketId,
								holderName: params.ticket.holderName,
								ticketTypeId: ticketType.ticketTypeId,
								extraProducts: [],
								/** generate cartChipIds */
								chips: importedGeneratedChips,
								/** add user data from fields schema */
								userData:
									ticketType.userData?.fields.reduce((acc, field) => {
										return {
											...acc,
											/** add field with default value */
											[field.fieldId]: field.defaultValue ?? (field.type === 'number' ? 0 : ''),
										};
									}, {}) ?? {},
								/** seating reservation */
								reservation: { crowd: null, seat: null },
							} satisfies CartStoreCartedTicketsSlice.CartedTicket;

							return { cartedTickets: [...state.cartedTickets, importedCartedTicket], ticketTypesConfig: updatedTicketTypesConfig };
						});
					},
					async removeCartedTickets({ cartTicketIds, trackingModule }) {
						/** get all carted tickets to remove */
						const cartedTicketsToRemove = cartTicketIds.reduce<Array<CartStoreCartedTicketsSlice.CartedTicket>>((acc, cartTicket) => {
							const { ticket, ticketType } = get().getCartedTicket({ cartTicketId: cartTicket });

							/** find other tickets auto-added with this ticket to remove */
							const autoAppendedCartedTickets =
								ticketType.extraData?.autoAppendTicketTypes?.reduce<Array<CartStoreCartedTicketsSlice.CartedTicket>>(
									(autoAppendAcc, autoTicketType) => {
										const autoAppendedTicket = get().cartedTickets.findLast((t) => {
											if (t.ticketTypeId !== autoTicketType.ticketTypeId) return false;
											if (autoAppendAcc.findIndex((at) => at.cartTicketId === t.cartTicketId) !== -1) return false;
											return true;
										});
										if (!isDefined(autoAppendedTicket)) return autoAppendAcc;
										return [...autoAppendAcc, autoAppendedTicket];
									},
									[],
								) ?? [];

							/** find other tickets to remove due to add to cart quantity config */
							const addToCartQtyCartedTickets =
								isDefined(ticketType.extraData?.addToCartQty) && ticketType.extraData?.addToCartQty > 1
									? arrayOf(ticketType.extraData?.addToCartQty - 1).reduce<Array<CartStoreCartedTicketsSlice.CartedTicket>>((addToCartAcc) => {
											const addToCartQtyTicket = get().cartedTickets.findLast((t) => {
												if (t.ticketTypeId !== ticketType.ticketTypeId) return false;
												if (addToCartAcc.findIndex((at) => at.cartTicketId === t.cartTicketId) !== -1) return false;
												if (autoAppendedCartedTickets.findIndex((at) => at.cartTicketId === t.cartTicketId) !== -1) return false;
												return true;
											});
											if (!isDefined(addToCartQtyTicket)) return addToCartAcc;
											return [...addToCartAcc, addToCartQtyTicket];
										}, [])
									: [];

							/** remove the ticket */
							log.debug(`[useCartTickets] Removing ticket type "${ticketType.namePub}" from the cart`);
							if (autoAppendedCartedTickets.length > 0) {
								log.debug(`[useCartTickets] Removing auto-appended tickets with indexes: ${autoAppendedCartedTickets.join(', ')}`);
							}
							if (addToCartQtyCartedTickets.length > 0) {
								log.debug(`[useCartTickets] Removing ${addToCartQtyCartedTickets.length} more tickets from the cart`);
							}

							return [...acc, ticket, ...autoAppendedCartedTickets, ...addToCartQtyCartedTickets];
						}, []);

						/** remove from form errors and touched fields */
						const cartedTicketsFormErrors = get().cartedTicketsFormErrors;
						const cartedTicketsTouchedFields = get().cartedTicketsTouchedFields;
						cartedTicketsToRemove.forEach((ticket) => {
							/** remove from form errors and touched fields */
							delete cartedTicketsFormErrors[ticket.cartTicketId];
							/** remove from touched fields */
							delete cartedTicketsTouchedFields[ticket.cartTicketId];
							/** reserved with seat */
							const seat = get().getSeatFromCartedTicket({ cartTicketId: ticket.cartTicketId });
							if (isDefined(seat)) {
								/** free seat reservation */
								get().removeSeatFromSeatingReservation({ seatId: seat.seatId });
							}
							/** reserved with crowd */
							const crowd = get().getCrowdFromCartedTicket({ cartTicketId: ticket.cartTicketId });
							if (isDefined(crowd)) {
								/** free crowd reservation */
								get().removeCrowdFromSeatingReservation({ crowdId: crowd.crowdId });
							}
							/** remove maximus products from carted tickets */
							get().removeMaximusCartedProductByCartedTicket({ cartTicketId: ticket.cartTicketId });
						});

						/** get new carted tickets list */
						const newCartedTickets = get().cartedTickets.filter((t) => {
							return cartedTicketsToRemove.findIndex((ct) => ct.cartTicketId === t.cartTicketId) === -1;
						});

						let currentCartView;
						if (newCartedTickets.length === 0) {
							/** reset view to ticket listing if no other tickets are in the cart */
							currentCartView = CART_VIEW.TICKETS;
							/** clear a reservation */
							const seatingReservation = get().seatingReservation;
							if (isDefined(seatingReservation)) {
								/** cancel reservation (using basePassApi instance, does not matter here) */
								await basePassApi.reservation.cancel.request({ reservationId: seatingReservation.reservationId });
								get().clearSeatingReservation();
							}
						}

						/** update the cart store */
						set(
							{
								/** update the carted tickets list */
								cartedTickets: newCartedTickets,
								/** update the current cart view if necessary */
								currentCartView: currentCartView ?? get().currentCartView,
								/** unselect the seat to close the sheet */
								selectedSeatId: null,
								/** unselect the crowd to close the sheet */
								selectedCrowdId: null,
							},
							undefined,
							'removeCartedTickets',
						);

						/** handle remove from cart event tracking (if provided) */
						if (isDefined(trackingModule)) {
							const cartConfig = get().cartConfig;
							trackingModule.safeTracking(() =>
								trackingModule.removeFromCart(
									{
										tickets: cartedTicketsToRemove.map((t) => ({
											cartTicketId: t.cartTicketId,
											ticketType: get().getTicketTypeById({ ticketTypeId: t.ticketTypeId }),
											chips: t.chips,
											extraProducts: [],
											reservation: t.reservation,
											holderName: t.holderName,
											userData: t.userData,
											trackingContext: cartConfig.eventContext,
										})),
										value: cartedTicketsToRemove.reduce((acc, t) => acc + get().getTicketTypeById({ ticketTypeId: t.ticketTypeId }).price, 0),
										currencyId: cartConfig.currencyId,
									},
									{ eventId: cartConfig.eventId },
								),
							);
						}
					},
					removeAllCartedTicketsByTicketType({ ticketTypeIds, trackingModule }) {
						/** get all carted tickets to remove */
						const cartedTicketsToRemove = get()
							.cartedTickets.filter((t) => ticketTypeIds.includes(t.ticketTypeId))
							.map((t) => t.cartTicketId);
						return get().removeCartedTickets({ cartTicketIds: cartedTicketsToRemove, trackingModule });
					},
					getCartedTicketHolderNameFallback({ cartTicketId }) {
						const snapshot = get();
						const contactDetails = snapshot.contactDetails;
						const otherCartedTickets = snapshot.cartedTickets;
						const holderNameFallbackFromContactDetails = !isEmpty(contactDetails.firstName) ? getFullName(contactDetails) : null;
						const holderNameFallbackFromCartedTickets = otherCartedTickets.length > 0 ? otherCartedTickets.at(-1)?.holderName : null;
						return holderNameFallbackFromContactDetails ?? holderNameFallbackFromCartedTickets ?? '';
					},
					addCartedTicketOfType({ quantity, ticketTypeId, input, trackingModule }) {
						const snapshot = get();
						const ticketType = snapshot.getTicketTypeById({ ticketTypeId });
						const cartedTicketIds = arrayOf(quantity).map(() => {
							return snapshot.addCartedTicket({ input: { ticketTypeId: ticketType.ticketTypeId, holderName: '', ...input }, trackingModule });
						});
						return cartedTicketIds;
					},
					removeCartedTicketOfType({ quantity, ticketTypeId, trackingModule }) {
						const cartedTicketsToRemove = get()
							.cartedTickets.filter((t) => t.ticketTypeId === ticketTypeId)
							.slice(0, quantity)
							.map((t) => t.cartTicketId);
						return get().removeCartedTickets({ cartTicketIds: cartedTicketsToRemove, trackingModule });
					},
					getCartedTicketsOfType({ ticketTypeId }) {
						return get().cartedTickets.filter((t) => t.ticketTypeId === ticketTypeId);
					},
					getCartedTicketTotal({ cartTicketId }) {
						const { ticket: cartedTicket, ticketType } = get().getCartedTicket({ cartTicketId });
						let ticketTypePrice = ticketType.price;
						if (ticketType.__isImported) {
							ticketTypePrice = 0;
						}
						const ticketTypeOriginalPrice = ticketType.extraData.originalPrice ?? ticketTypePrice;
						const ticketChips = cartedTicket.chips.reduce((chipSum, chip) => chipSum + chip.amount, 0);
						const ticketExtraProducts = cartedTicket.extraProducts.reduce((extraProductsChipsSum, extraProduct) => {
							const extraProductConfig = get().getExtraProductById({ ticketProductId: extraProduct.ticketProductId });
							return extraProductsChipsSum + extraProduct.count * extraProductConfig.price;
						}, 0);
						const ticketExtraProductsOriginal = ticketExtraProducts;
						const ticketExtraProductChips = cartedTicket.extraProducts.reduce((extraProductsChipsSum, extraProduct) => {
							const extraProductChips = extraProduct.chips.reduce(
								(extraProductChipSum, extraProductChip) => extraProductChipSum + extraProductChip.amount,
								0,
							);
							return extraProductsChipsSum + extraProductChips;
						}, 0);

						return {
							ticket: ticketTypePrice,
							ticketOriginal: ticketTypeOriginalPrice,
							extraProducts: ticketExtraProducts,
							extraProductsOriginal: ticketExtraProductsOriginal,
							extraProductChips: ticketExtraProductChips,
							chips: ticketChips,
							total: ticketTypePrice + ticketChips + ticketExtraProducts + ticketExtraProductChips,
							totalOriginal: ticketTypeOriginalPrice + ticketChips + ticketExtraProductsOriginal + ticketExtraProductChips,
						};
					},
					getCartedTicketsTotal() {
						return get().cartedTickets.reduce(
							(acc, ticket) => {
								const t = get().getCartedTicketTotal({ cartTicketId: ticket.cartTicketId });
								return {
									ticket: acc.ticket + t.ticket,
									ticketOriginal: acc.ticketOriginal + t.ticketOriginal,
									extraProducts: acc.extraProducts + t.extraProducts,
									extraProductsOriginal: acc.extraProductsOriginal + t.extraProductsOriginal,
									chips: acc.chips + t.chips,
									extraProductChips: acc.extraProductChips + t.extraProductChips,
									total: acc.total + t.total,
									totalOriginal: acc.totalOriginal + t.totalOriginal,
								};
							},
							{
								ticket: 0,
								ticketOriginal: 0,
								extraProducts: 0,
								extraProductsOriginal: 0,
								chips: 0,
								extraProductChips: 0,
								total: 0,
								totalOriginal: 0,
							},
						);
					},
					getCartedTicketTypes() {
						return uniqBy(
							get()
								.cartedTickets.map((t) => get().getTicketTypeById({ ticketTypeId: t.ticketTypeId }))
								.sort(sortBy('displayOrder', { direction: 'asc' })),
							'ticketTypeId',
						);
					},
					extraProductsConfig: [],
					loadExtraProductsConfig({ extraProducts, duplicatesStrategy, forceOverwrite }) {
						return set(
							(snapshot) => {
								const extraProductsConfig = loadConfigWithStrategy(snapshot.extraProductsConfig, extraProducts, {
									duplicatesStrategy,
									forceOverwrite,
									identifier: (tt) => tt.ticketProductId,
								}).sort(sortBy('displayOrder', { direction: 'asc' }));

								return { extraProductsConfig };
							},
							undefined,
							'loadExtraProductsConfig',
						);
					},

					extraProductGroupsConfig: [],
					loadExtraProductGroupsConfig({ extraProductGroups, duplicatesStrategy, forceOverwrite }) {
						return set(
							(snapshot) => {
								const extraProductGroupsConfig = loadConfigWithStrategy(snapshot.extraProductGroupsConfig, extraProductGroups, {
									duplicatesStrategy,
									forceOverwrite,
									identifier: (tt) => tt.productGroupId,
								}).sort(sortBy('displayOrder', { direction: 'asc' }));

								return { extraProductGroupsConfig };
							},
							undefined,
							'loadExtraProductGroupsConfig',
						);
					},

					getExtraProductById({ ticketProductId }) {
						const snapshot = get();
						const extraProduct = snapshot.extraProductsConfig.find((ep) => ep.ticketProductId === ticketProductId);
						if (!isDefined(extraProduct)) {
							throw new InvalidStateError(`Could not find extra product with id "${ticketProductId}"`);
						}
						return extraProduct;
					},

					getExtraProductGroupById({ productGroupId }) {
						const snapshot = get();
						const extraProductGroup = snapshot.extraProductGroupsConfig.find((epg) => epg.productGroupId === productGroupId);
						if (!isDefined(extraProductGroup)) {
							throw new InvalidStateError(`Could not find product group with id "${productGroupId}"`);
						}
						return extraProductGroup;
					},

					getExtraProductByIdWithQuantityLimit({ ticketProductId, ticketTypeId }) {
						const snapshot = get();
						const ticketType = snapshot.getTicketTypeById({ ticketTypeId });
						const ticketTypeExtraProduct = ticketType.extraProducts.find((ep) => ep.ticketProductId === ticketProductId);
						if (!isDefined(ticketTypeExtraProduct)) {
							throw new InvalidStateError(`Could not find extra product with id "${ticketProductId}" within ticket type "${ticketTypeId}"`);
						}
						return { ...snapshot.getExtraProductById({ ticketProductId }), quantityLimit: ticketTypeExtraProduct.quantityLimit };
					},

					getExtraProductChipCount({ ticketProductId, ticketTypeId }) {
						const snapshot = get();
						const ticketType = snapshot.getTicketTypeById({ ticketTypeId });
						const extraProduct = snapshot.getExtraProductById({ ticketProductId });

						/** only count chips if ticket type has available credit limit */
						return ticketType.creditLimit > ticketType.creditMinimum
							? extraProduct.productItems.reduce((acc, item) => acc + (item.itemTypeId === ITEM_TYPE.CHIP ? item.quantity : 0), 0)
							: 0;
					},

					getExtraProductInitialChips({ ticketProductId, ticketTypeId }) {
						const snapshot = get();
						const ticketType = snapshot.getTicketTypeById({ ticketTypeId });
						const extraProduct = snapshot.getExtraProductById({ ticketProductId });

						/** no chips if ticketType's creditLimit is set to 0 (or less than creditMinimum) */
						return ticketType.creditLimit > ticketType.creditMinimum
							? extraProduct.productItems.reduce<Array<CartStoreCartedTicketsSlice.CartedTicketChip>>(
									(acc, item) =>
										item.itemTypeId === ITEM_TYPE.CHIP
											? [...acc, ...arrayOf(item.quantity).map(() => ({ cartChipId: uuidv4(), amount: 0, productItemId: item.productItemId }))]
											: acc,
									[],
								)
							: [];
					},

					getExtraProductGroupProducts({ productGroupId, ticketTypeId }) {
						const snapshot = get();
						const productGroup = snapshot.getExtraProductGroupById({ productGroupId });
						const ticketType = snapshot.getTicketTypeById({ ticketTypeId });

						return snapshot.extraProductsConfig.reduce<Array<CartStoreExtraProductsSlice.ExtraProductConfig & { quantityLimit: number }>>(
							(acc, product) => {
								/** filter out extra products with different product group */
								if (product.productGroupId !== productGroup.productGroupId) return acc;

								/** filter out disallowed extra products */
								const productInTicketType = ticketType.extraProducts.find((t) => t.ticketProductId === product.ticketProductId);
								if (!isDefined(productInTicketType)) return acc;

								return [...acc, { ...product, quantityLimit: productInTicketType.quantityLimit }];
							},
							[],
						);
					},

					getExtraProductGroupPriceRange({ productGroupId, ticketTypeId }) {
						const snapshot = get();
						const products = snapshot.getExtraProductGroupProducts({ productGroupId, ticketTypeId });
						const prices = products.map((p) => p.price);
						return { min: Math.min(...prices), max: Math.max(...prices) };
					},

					getSimpleProductControls({ ticketTypeId }) {
						const snapshot = get();
						const ticketType = snapshot.getTicketTypeById({ ticketTypeId });

						// Get products that are not in any group and are allowed for this ticket type
						return snapshot.extraProductsConfig
							.filter(
								(product) => isEmpty(product.productGroupId) && ticketType.extraProducts.some((tp) => tp.ticketProductId === product.ticketProductId),
							)
							.map((product) => ({
								type: CartStoreExtraProductsSlice.PRODUCT_CONTROL_TYPE.SIMPLE,
								displayOrder: product.displayOrder,
								ticketProductId: product.ticketProductId,
							}));
					},

					getProductGroupControls({ ticketTypeId }) {
						const snapshot = get();

						return snapshot.extraProductGroupsConfig.reduce<
							Array<{ type: CartStoreExtraProductsSlice.PRODUCT_CONTROL_TYPE.GROUP; displayOrder: number; productGroupId: UUID }>
						>((acc, group) => {
							/** get product group products */
							const products = snapshot.getExtraProductGroupProducts({ productGroupId: group.productGroupId, ticketTypeId });

							/** filter out groups with no products to offer */
							if (products.length === 0) return acc;

							/** get display orders of all product group products */
							const displayOrders = products.map((p) => p.displayOrder);

							return [
								...acc,
								{
									type: CartStoreExtraProductsSlice.PRODUCT_CONTROL_TYPE.GROUP,
									displayOrder: Math.min(...displayOrders),
									productGroupId: group.productGroupId,
								},
							];
						}, []);
					},

					getProductControls({ ticketTypeId }) {
						const snapshot = get();
						const simpleProducts = snapshot.getSimpleProductControls({ ticketTypeId });
						const groupedProducts = snapshot.getProductGroupControls({ ticketTypeId });

						// Combine and sort all product controls by display order
						return [...simpleProducts, ...groupedProducts].sort(sortBy('displayOrder', { direction: 'asc' }));
					},

					isExtraProductConfigurationRequired({ ticketProductId, ticketTypeId }) {
						const snapshot = get();
						const extraProduct = snapshot.getExtraProductByIdWithQuantityLimit({ ticketProductId, ticketTypeId });

						/** config needed when optional text regex is set */
						if (!isEmpty(extraProduct.optionalTextRegex)) return true;

						/** config needed when chips are available for top up along the product */
						if (snapshot.getExtraProductChipCount({ ticketProductId, ticketTypeId }) > 0) return true;

						return false;
					},

					addExtraProductToCartedTicket({ ticketProductId, cartTicketId, initialValue }) {
						return set(
							(snapshot) => {
								const cartedTickets = snapshot.cartedTickets.map((ticket) => {
									if (ticket.cartTicketId !== cartTicketId) return ticket;

									return {
										...ticket,
										extraProducts: [
											...ticket.extraProducts,
											{ cartProductId: uuidv4(), ticketProductId, count: 1, optionalText: '', chips: [], ...initialValue },
										],
									};
								});

								return { cartedTickets };
							},
							undefined,
							'addExtraProductToCartedTicket',
						);
					},

					updateExtraProductInCartedTicket({ cartProductId, cartTicketId, update }) {
						return set(
							(snapshot) => {
								const cartedTickets = snapshot.cartedTickets.map((ticket) => {
									if (ticket.cartTicketId !== cartTicketId) return ticket;

									const updatedExtraProducts = ticket.extraProducts.map((product) => {
										if (product.cartProductId !== cartProductId) return product;
										return { ...product, ...update };
									});

									return { ...ticket, extraProducts: updatedExtraProducts };
								});

								return { cartedTickets };
							},
							undefined,
							'updateExtraProductInCartedTicket',
						);
					},

					removeExtraProductFromCartedTicket({ cartProductId, cartTicketId }) {
						return set(
							(snapshot) => {
								const cartedTickets = snapshot.cartedTickets.map((ticket) => {
									if (ticket.cartTicketId !== cartTicketId) return ticket;

									return { ...ticket, extraProducts: ticket.extraProducts.filter((ep) => ep.cartProductId !== cartProductId) };
								});

								return { cartedTickets };
							},
							undefined,
							'removeExtraProductFromCartedTicket',
						);
					},

					getExtraProductStateById({ ticketProductId, cartTicketId, cartProductId }) {
						const snapshot = get();
						const { ticket } = snapshot.getCartedTicket({ cartTicketId });

						/** count products within the ticket (excluding the current product if updating) */
						const productWithinTicketCount = ticket.extraProducts
							.filter((p) => p.ticketProductId === ticketProductId && p.cartProductId !== cartProductId)
							.reduce((acc, product) => acc + product.count, 0);

						/** get product config with quantity limit */
						const extraProduct = snapshot.getExtraProductByIdWithQuantityLimit({ ticketProductId, ticketTypeId: ticket.ticketTypeId });

						/** count all products across all tickets */
						const allCartedProductsWithinTicketsCount = snapshot.cartedTickets
							.flatMap((t) => t.extraProducts)
							.filter((p) => p.ticketProductId === ticketProductId && p.cartProductId !== cartProductId)
							.reduce((acc, product) => acc + product.count, 0);

						return {
							quantityLimited: productWithinTicketCount >= extraProduct.quantityLimit,
							soldOut: allCartedProductsWithinTicketsCount >= extraProduct.productsAvailable,
							productWithinTicketCount,
							allCartedProductsWithinTicketsCount,
						};
					},

					getCartedExtraProduct({ cartProductId, cartTicketId }) {
						const snapshot = get();
						const { ticket } = snapshot.getCartedTicket({ cartTicketId });
						const extraProduct = ticket.extraProducts.find((ep) => ep.cartProductId === cartProductId);

						if (!isDefined(extraProduct)) {
							throw new InvalidStateError(`Could not find extra product with id "${cartProductId}" in cart ticket "${cartTicketId}"`);
						}

						const extraProductConfig = snapshot.getExtraProductById({ ticketProductId: extraProduct.ticketProductId });
						return { extraProduct, extraProductConfig, ticket };
					},

					getCartedExtraProductChipAmount({ cartProductId, cartTicketId }) {
						const snapshot = get();
						const { ticket } = snapshot.getCartedTicket({ cartTicketId });
						const extraProduct = ticket.extraProducts.find((ep) => ep.cartProductId === cartProductId);
						if (!isDefined(extraProduct)) return 0;
						return extraProduct.chips.reduce((acc, chip) => acc + chip.amount, 0);
					},

					getCartedExtraProductPrice({ cartProductId, cartTicketId, ticketProductId }) {
						const snapshot = get();
						const cartedExtraProduct = silenceError(() => {
							if (isEmpty(cartProductId)) return null;
							return snapshot.getCartedExtraProduct({ cartProductId, cartTicketId });
						}, null);
						const extraProductConfig = silenceError(() => {
							if (isDefined(cartedExtraProduct)) return cartedExtraProduct.extraProductConfig;
							if (isEmpty(ticketProductId)) return null;
							return snapshot.getExtraProductById({ ticketProductId });
						}, null);

						const basePrice = extraProductConfig?.price ?? 0;
						const chipAmount = cartedExtraProduct?.extraProduct.chips.reduce((acc, chip) => acc + chip.amount, 0) ?? 0;
						const count = cartedExtraProduct?.extraProduct.count ?? 1;
						return (chipAmount + basePrice) * count;
					},

					getExtraProductGroupStateById({ productGroupId, cartTicketId }) {
						const snapshot = get();
						/** get the scoped ticket and product group */
						const { ticket } = snapshot.getCartedTicket({ cartTicketId });
						const productGroup = snapshot.getExtraProductGroupById({ productGroupId });

						/** get all product group products */
						const products = snapshot.getExtraProductGroupProducts({ productGroupId, ticketTypeId: ticket.ticketTypeId });

						return products.reduce<ReturnType<CartStore.Store['getExtraProductGroupStateById']>>(
							(acc, product) => {
								/** get meta for the given product */
								const productIs = snapshot.getExtraProductStateById({ ticketProductId: product.ticketProductId, cartTicketId });
								const isGroupQuantityLimited =
									productGroup.quantityLimit === -1 ? false : productIs.productWithinTicketCount >= productGroup.quantityLimit;
								const isQuantityLimited = productIs.quantityLimited || isGroupQuantityLimited;
								return {
									/** quantity limited if previously was and currently is */
									quantityLimited: acc.quantityLimited || isQuantityLimited,
									/** sold out if previously was and currently is */
									soldOut: acc.soldOut && productIs.soldOut,
								};
							},
							{ quantityLimited: false, soldOut: true },
						);
					},

					extraProductConfigSheetProductInput: null,
					setExtraProductConfigSheetProductInput(input) {
						return set({ extraProductConfigSheetProductInput: input }, undefined, 'setExtraProductConfigSheetProductInput');
					},

					extraProductConfigSheetVariantsInput: null,
					setExtraProductConfigSheetVariantsInput(input) {
						return set({ extraProductConfigSheetVariantsInput: input }, undefined, 'setExtraProductConfigSheetVariantsInput');
					},
					/** seating */
					venueConfig: null,
					loadVenueConfig({ venue }) {
						return set({ venueConfig: venue }, undefined, 'loadVenueConfig');
					},
					sectorsConfig: [],
					loadSectorsConfig({ sectors, duplicatesStrategy, forceOverwrite }) {
						return set(
							(snapshot) => {
								const sectorsConfig = loadConfigWithStrategy(snapshot.sectorsConfig, sectors, {
									duplicatesStrategy,
									forceOverwrite,
									identifier: (s) => s.sectorId,
								});
								return { sectorsConfig };
							},
							undefined,
							'loadSectorsConfig',
						);
					},
					openedSectorId: null,
					openSector({ sectorId }) {
						return set(
							(snapshot) => {
								const sector = snapshot.getSectorById({ sectorId });
								return { openedSectorId: sector.sectorId };
							},
							undefined,
							'openSector',
						);
					},
					closeSector() {
						return set({ openedSectorId: null }, undefined, 'closeSector');
					},
					getSectorById({ sectorId }) {
						const snapshot = get();
						const sector = snapshot.sectorsConfig.find((s) => s.sectorId === sectorId);
						if (!isDefined(sector)) {
							throw new InvalidStateError(`Could not find sector with id "${sectorId}"`);
						}
						return sector;
					},
					getSectorByIdWithSeatsAndCrowds({ sectorId }) {
						const snapshot = get();
						const sector = snapshot.getSectorById({ sectorId });
						const seats = snapshot.seatsConfig.filter((s) => s.sectorId === sector.sectorId);
						const crowds = snapshot.crowdsConfig.filter((c) => c.sectorId === sector.sectorId);
						return { ...sector, seats, crowds };
					},
					getSectorStateById({ sectorId }) {
						const snapshot = get();
						const sector = snapshot.getSectorById({ sectorId });

						/** no capacity left in given sector */
						const soldOut = sector.capacityLeft <= 0;
						return { soldOut, opened: snapshot.openedSectorId === sectorId };
					},
					seatsConfig: [],
					loadSeatsConfig({ seats, duplicatesStrategy, forceOverwrite }) {
						return set(
							(snapshot) => {
								const seatsConfig = loadConfigWithStrategy(snapshot.seatsConfig, seats, {
									duplicatesStrategy,
									forceOverwrite,
									identifier: (s) => s.seatId,
								});
								return { seatsConfig };
							},
							undefined,
							'loadSeatsConfig',
						);
					},
					getSeatById({ seatId }) {
						const snapshot = get();
						const seat = snapshot.seatsConfig.find((s) => s.seatId === seatId);
						if (!isDefined(seat)) {
							throw new InvalidStateError(`Could not find seat with id "${seatId}"`);
						}
						return seat;
					},
					getSeatStateById({ seatId }) {
						const snapshot = get();
						const seat = snapshot.getSeatById({ seatId });

						/** calculate seat availability states */
						const carted = !isEmpty(seat.reservationSeatId);
						const soldOut = seat.capacityLeft <= 0 && !carted;
						const selected = snapshot.selectedSeatId === seat.seatId;
						const saleable = seat.ticketTypesAvailable > 0 && !seat.unsalable;
						const unavailable = !saleable || soldOut;

						return { soldOut, selected, saleable, unavailable, carted };
					},
					getSeatFromCartedTicket({ cartTicketId }) {
						const snapshot = get();
						const ticket = snapshot.cartedTickets.find((t) => t.cartTicketId === cartTicketId);
						if (!isDefined(ticket)) {
							throw new InvalidStateError(`Could not find ticket with id "${cartTicketId}"`);
						}
						if (!isDefined(ticket.reservation.seat)) return null;
						return silenceError(() => snapshot.getSeatById({ seatId: ticket.reservation.seat!.seatId }), null);
					},
					getCartedTicketFromSeat({ seatId }) {
						const snapshot = get();
						const ticket = snapshot.cartedTickets.find((t) => {
							if (!isDefined(t.reservation.seat)) return false;
							return t.reservation.seat.seatId === seatId;
						});
						return ticket ?? null;
					},
					selectedSeatId: null,
					selectSeat({ seatId }) {
						return set(
							(snapshot) => {
								const seat = snapshot.getSeatById({ seatId });
								return { selectedSeatId: seat.seatId };
							},
							undefined,
							'selectSeat',
						);
					},
					unselectSeat() {
						return set({ selectedSeatId: null }, undefined, 'unselectSeat');
					},
					/** crowds */
					crowdsConfig: [],
					loadCrowdsConfig({ crowds, duplicatesStrategy, forceOverwrite }) {
						return set(
							(snapshot) => {
								const crowdsConfig = loadConfigWithStrategy(snapshot.crowdsConfig, crowds, {
									duplicatesStrategy,
									forceOverwrite,
									identifier: (c) => c.crowdId,
								});
								return { crowdsConfig };
							},
							undefined,
							'loadCrowdsConfig',
						);
					},
					getCrowdById({ crowdId }) {
						const snapshot = get();
						const crowd = snapshot.crowdsConfig.find((c) => c.crowdId === crowdId);
						if (!isDefined(crowd)) {
							throw new InvalidStateError(`Could not find crowd with id "${crowdId}"`);
						}
						return crowd;
					},
					getCrowdStateById({ crowdId }) {
						const snapshot = get();
						const crowd = snapshot.getCrowdById({ crowdId });

						/** Calculate crowd availability states */
						const carted = !isEmpty(crowd.reservationCrowdId);
						const soldOut = crowd.capacityLeft <= 0 && !carted;
						const selected = snapshot.selectedCrowdId === crowd.crowdId;
						const saleable = crowd.ticketTypesAvailable > 0;
						const unavailable = !saleable || soldOut;

						return { soldOut, selected, saleable, unavailable, carted };
					},
					getCrowdFromCartedTicket({ cartTicketId }) {
						const snapshot = get();
						const ticket = snapshot.cartedTickets.find((t) => t.cartTicketId === cartTicketId);
						if (!isDefined(ticket)) {
							throw new InvalidStateError(`Could not find ticket with id "${cartTicketId}"`);
						}
						if (!isDefined(ticket.reservation.crowd)) return null;
						return silenceError(() => snapshot.getCrowdById({ crowdId: ticket.reservation.crowd!.crowdId }), null);
					},
					getCartedTicketsFromCrowd({ crowdId }) {
						const snapshot = get();
						return snapshot.cartedTickets.filter((t) => {
							if (!isDefined(t.reservation.crowd)) return false;
							return t.reservation.crowd.crowdId === crowdId;
						});
					},
					selectedCrowdId: null,
					selectCrowd({ crowdId }) {
						return set(
							(snapshot) => {
								const crowd = snapshot.getCrowdById({ crowdId });
								return { selectedCrowdId: crowd.crowdId };
							},
							undefined,
							'selectCrowd',
						);
					},
					unselectCrowd() {
						return set({ selectedCrowdId: null }, undefined, 'unselectCrowd');
					},
					/** reservation */
					seatingReservation: null,
					createSeatingReservation({ reservation }) {
						return set({ seatingReservation: reservation }, undefined, 'createSeatingReservation');
					},
					addSeatToSeatingReservation({ seatId, reservationSeatId }) {
						return set(
							(snapshot) => {
								if (!snapshot.seatingReservation) throw new InvalidStateError('No seating reservation found');
								return {
									seatsConfig: snapshot.seatsConfig.map((seat) => (seat.seatId === seatId ? { ...seat, reservationSeatId } : seat)),
									seatingReservation: {
										...snapshot.seatingReservation,
										seats: [...snapshot.seatingReservation.seats.filter((s) => s.seatId !== seatId), { seatId, reservationSeatId }],
									},
								};
							},
							undefined,
							'addSeatToSeatingReservation',
						);
					},
					removeSeatFromSeatingReservation({ seatId }) {
						return set(
							(snapshot) => {
								if (!snapshot.seatingReservation) throw new InvalidStateError('No seating reservation found');
								return {
									seatsConfig: snapshot.seatsConfig.map((seat) => (seat.seatId === seatId ? { ...seat, reservationSeatId: null } : seat)),
									seatingReservation: { ...snapshot.seatingReservation, seats: snapshot.seatingReservation.seats.filter((s) => s.seatId !== seatId) },
								};
							},
							undefined,
							'removeSeatFromSeatingReservation',
						);
					},
					addCrowdToSeatingReservation({ crowdId, reservationCrowdId }) {
						return set(
							(snapshot) => {
								if (!snapshot.seatingReservation) throw new InvalidStateError('No seating reservation found');
								return {
									seatingReservation: {
										...snapshot.seatingReservation,
										crowds: [...snapshot.seatingReservation.crowds.filter((c) => c.crowdId !== crowdId), { crowdId, reservationCrowdId }],
									},
								};
							},
							undefined,
							'addCrowdToSeatingReservation',
						);
					},
					removeCrowdFromSeatingReservation({ crowdId }) {
						return set(
							(snapshot) => {
								if (!snapshot.seatingReservation) throw new InvalidStateError('No seating reservation found');
								return {
									seatingReservation: {
										...snapshot.seatingReservation,
										crowds: snapshot.seatingReservation.crowds.filter((c) => c.crowdId !== crowdId),
									},
								};
							},
							undefined,
							'removeCrowdFromSeatingReservation',
						);
					},
					clearSeatingReservation() {
						const snapshot = get();
						return set(
							{
								/** clear the reservation */
								seatingReservation: null,
								/** clear seat reservations */
								seatsConfig: snapshot.seatsConfig.map((seat) => ({ ...seat, reservationSeatId: null })),
								/** clear crowd reservations */
								crowdsConfig: snapshot.crowdsConfig.map((crowd) => ({ ...crowd, reservationCrowdId: null })),
								/** clear selected seat */
								selectedSeatId: null,
								/** clear seats/crowds from tickets */
								cartedTickets: snapshot.cartedTickets.filter((t) => !isDefined(t.reservation.seat) && !isDefined(t.reservation.crowd)),
							},
							undefined,
							'clearSeatingReservation',
						);
					},
					categoriesConfig: [],
					loadCategoriesConfig({ categories, duplicatesStrategy, forceOverwrite }) {
						return set(
							(snapshot) => {
								const categoriesConfig = loadConfigWithStrategy(snapshot.categoriesConfig, categories, {
									duplicatesStrategy,
									forceOverwrite,
									identifier: (c) => c.categoryId,
								});
								return { categoriesConfig };
							},
							undefined,
							'loadCategoriesConfig',
						);
					},
					getCategoryById({ categoryId }) {
						const snapshot = get();
						const category = snapshot.categoriesConfig.find((c) => c.categoryId === categoryId);
						if (!isDefined(category)) {
							throw new InvalidStateError(`Could not find category with id "${categoryId}"`);
						}
						return category;
					},
					getCategoryTicketTypes({ categoryId }) {
						const snapshot = get();
						const ticketTypesListed = cartStoreTicketTypesSelectors.listTicketTypes(snapshot);
						return ticketTypesListed
							.filter((tt) => tt.categories.some((c) => c.categoryId === categoryId))
							.sort(sortBy('displayOrder', { direction: 'asc' }));
					},
					/** maximus */
					maximusProductsConfig: [],
					loadMaximusProductsConfig({ maximusProducts, duplicatesStrategy, forceOverwrite }) {
						return set(
							(snapshot) => {
								const maximusProductsConfig = loadConfigWithStrategy(snapshot.maximusProductsConfig, maximusProducts, {
									duplicatesStrategy,
									forceOverwrite,
									identifier: (p) => p.maximusProductId,
								});
								return { maximusProductsConfig };
							},
							undefined,
							'loadMaximusProductsConfig',
						);
					},
					getMaximusProductsForTicketType({ ticketTypeId }) {
						const snapshot = get();
						return snapshot.maximusProductsConfig.reduce<Array<CartStoreMaximusSlice.MaximusProductConfigForTicketType>>((acc, curr) => {
							const maximusProduct = snapshot.maximusProductsConfig.find((mp) => mp.maximusProductId === curr.maximusProductId);
							if (!isDefined(maximusProduct)) return acc;
							const maximusProductTicketType = maximusProduct.ticketTypes.find((tt) => tt.ticketTypeId === ticketTypeId);
							if (!isDefined(maximusProductTicketType)) return acc;
							return [
								...acc,
								{ ...maximusProduct, minQuantity: maximusProductTicketType.minQuantity, maxQuantity: maximusProductTicketType.maxQuantity },
							];
						}, []);
					},
					getMaximusProductsForPaymentMethod({ paymentMethodId }) {
						const snapshot = get();
						return snapshot.maximusProductsConfig.reduce<Array<CartStoreMaximusSlice.MaximusProductConfigForPaymentMethod>>((acc, curr) => {
							const maximusProduct = snapshot.maximusProductsConfig.find((mp) => mp.maximusProductId === curr.maximusProductId);
							if (!isDefined(maximusProduct)) return acc;
							const maximusProductPaymentMethod = maximusProduct.paymentMethods.find((pm) => pm.paymentMethodId === paymentMethodId);
							if (!isDefined(maximusProductPaymentMethod)) return acc;
							return [
								...acc,
								{ ...maximusProduct, minQuantity: maximusProductPaymentMethod.minQuantity, maxQuantity: maximusProductPaymentMethod.maxQuantity },
							];
						}, []);
					},
					maximusCartedProducts: [],
					addMaximusCartedProduct({ input }) {
						return set(
							(snapshot) => {
								// TODO: check if we can add maximus product to carted ticket (not sold out, not limited)
								const maximusProductState = snapshot.getMaximusProductStateById({ maximusProductId: input.maximusProductId });
								if (maximusProductState.soldOut) {
									log.warn('Maximus product is sold out (ignoring)', { maximusProductId: input.maximusProductId });
									return {};
								}

								return {
									maximusCartedProducts: [
										...snapshot.maximusCartedProducts,
										{
											cartMaximusProductId: uuidv4(),
											maximusProductId: input.maximusProductId,
											count: input.count,
											cartTicketId: input.cartTicketId,
											paymentMethodId: input.paymentMethodId,
										},
									],
								};
							},
							undefined,
							'addMaximusCartedProduct',
						);
					},
					removeMaximusCartedProduct({ cartMaximusProductId }) {
						return set(
							(snapshot) => {
								return { maximusCartedProducts: snapshot.maximusCartedProducts.filter((p) => p.cartMaximusProductId !== cartMaximusProductId) };
							},
							undefined,
							'removeMaximusCartedProduct',
						);
					},
					getMaximusProductById({ maximusProductId }) {
						const snapshot = get();
						return valOrThrow(
							snapshot.maximusProductsConfig.find((p) => p.maximusProductId === maximusProductId),
							`Could not find maximus product with id "${maximusProductId}"`,
						);
					},
					getCartedMaximusProductsForCartedTicket({ cartTicketId }) {
						const snapshot = get();
						return snapshot.maximusCartedProducts
							.filter((maximusProduct) => maximusProduct.cartTicketId === cartTicketId)
							.map((maximusProduct) => {
								const maximusProductConfig = snapshot.getMaximusProductById({ maximusProductId: maximusProduct.maximusProductId });
								return { maximusProductConfig, maximusProduct };
							});
					},
					getMaximusProductStateById({ maximusProductId }) {
						const snapshot = get();
						const maximusProductConfig = snapshot.getMaximusProductById({ maximusProductId });
						const allCartedMaximusProductsCount = snapshot.maximusCartedProducts.reduce((acc, curr) => {
							if (curr.maximusProductId === maximusProductId) {
								return acc + curr.count;
							}
							return acc;
						}, 0);

						const isSoldOut =
							maximusProductConfig.productsAvailable === -1 ? false : maximusProductConfig.productsAvailable - allCartedMaximusProductsCount <= 0;

						return { soldOut: isSoldOut };
					},
					getCartedMaximusProductsTotal({ cartTicketId }) {
						const snapshot = get();
						const cartedProducts = isEmpty(cartTicketId)
							? snapshot.maximusCartedProducts
							: snapshot.getCartedMaximusProductsForCartedTicket({ cartTicketId }).map((mp) => mp.maximusProduct);
						const breakdown = cartedProducts.reduce(
							(acc, curr) => {
								const maximusProductConfig = snapshot.getMaximusProductById({ maximusProductId: curr.maximusProductId });
								switch (maximusProductConfig.productTypeId) {
									case CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TICKET_PROCESSING_FEE:
									case CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.PAYMENT_PROCESSING_FEE: {
										return {
											fees: acc.fees + curr.count * maximusProductConfig.price,
											products: acc.products,
											/** NOTE: just preparation for possible future use of sales/discounts */
											feesOriginal: acc.feesOriginal + curr.count * maximusProductConfig.price,
											productsOriginal: acc.productsOriginal,
										};
									}
									case CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TICKET_GIFT_WRAP:
									case CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TICKET_GIFT_WRAP_DELIVERY:
									case CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TREE_DONATION:
									default: {
										return {
											fees: acc.fees,
											products: acc.products + curr.count * maximusProductConfig.price,
											/** NOTE: just preparation for possible future use of sales/discounts */
											feesOriginal: acc.feesOriginal,
											productsOriginal: acc.productsOriginal + curr.count * maximusProductConfig.price,
										};
									}
								}
							},
							{ fees: 0, products: 0, feesOriginal: 0, productsOriginal: 0 },
						);

						return {
							fees: breakdown.fees,
							products: breakdown.products,
							total: breakdown.products + breakdown.fees,
							/** NOTE: just preparation for possible future use of sales/discounts */
							feesOriginal: breakdown.feesOriginal,
							productsOriginal: breakdown.productsOriginal,
							totalOriginal: breakdown.productsOriginal + breakdown.feesOriginal,
						};
					},
					getCartTotal() {
						const snapshot = get();
						const cartedTicketsTotal = snapshot.getCartedTicketsTotal();
						const maximusProductsTotal = snapshot.getCartedMaximusProductsTotal({ cartTicketId: null });
						return {
							/** from carted tickets */
							totalTickets: cartedTicketsTotal.ticket,
							originalTotalTickets: cartedTicketsTotal.ticketOriginal,
							totalChips: cartedTicketsTotal.chips,
							originalTotalChips: cartedTicketsTotal.chips,
							totalExtraProducts: cartedTicketsTotal.extraProducts,
							originalTotalExtraProducts: cartedTicketsTotal.extraProductsOriginal,
							/** from maximus products */
							totalMaximusProducts: maximusProductsTotal.products,
							originalTotalMaximusProducts: maximusProductsTotal.productsOriginal,
							totalMaximusProcessingFees: maximusProductsTotal.fees,
							originalTotalMaximusProcessingFees: maximusProductsTotal.feesOriginal,
							/** total */
							total: cartedTicketsTotal.total + maximusProductsTotal.total,
							originalTotal: cartedTicketsTotal.totalOriginal + maximusProductsTotal.totalOriginal,
						};
					},
					removeMaximusCartedProductByPaymentMethod({ paymentMethodId }) {
						return set((snapshot) => {
							return { maximusCartedProducts: snapshot.maximusCartedProducts.filter((mp) => mp.paymentMethodId !== paymentMethodId) };
						});
					},
					removeMaximusCartedProductByCartedTicket({ cartTicketId }) {
						return set((snapshot) => {
							return { maximusCartedProducts: snapshot.maximusCartedProducts.filter((mp) => mp.cartTicketId !== cartTicketId) };
						});
					},
				} satisfies CartStore.Store;
			},
			{
				name: 'cartStore',
				enabled: IS_LOCAL,
				serialize: {
					/** NOTE: this will not correctly convert back to Set and Map when rolling state back */
					options: { set: true, map: true },
				},
			},
		),
	);
}

/* -------------------------------------------------------------------------------------------------
 * Cart Store / Context & Hooks
 * TODO: probably move to separate hooks.ts file
 * -----------------------------------------------------------------------------------------------*/
const CartStoreContext = React.createContext<(UseBoundStore<StoreApi<CartStore.Store>> & ZustandHookSelectors<CartStore.Store>) | null>(null);

export const CartStoreProvider: React.FC<{ children?: React.ReactNode; initialValue: CartStore.InitParams }> = (props) => {
	const [store] = React.useState(() => {
		const base = createSelectorHooks(createCartStore(props.initialValue));
		return base;
	});
	return <CartStoreContext value={store}>{props.children}</CartStoreContext>;
};

export function useCartStoreCtx() {
	const store = useContextRequired(CartStoreContext, 'CartStoreProvider is missing from the component tree');
	return store;
}
export function useCartStore<T>(selector: (state: CartStore.Store) => T, equality: Equality<T | undefined> = 'shallow'): T {
	const store = useCartStoreCtx();
	return useStore(store, useEquality(selector, equality));
}

export function useCartStoreExtraProductsProvider() {
	/** sheet config */
	const sheetConfig = useCartStore((state) => ({
		productInput: state.extraProductConfigSheetProductInput,
		setProductInput: state.setExtraProductConfigSheetProductInput,
		variantsInput: state.extraProductConfigSheetVariantsInput,
		setVariantsInput: state.setExtraProductConfigSheetVariantsInput,
	}));

	/** extra products */
	const productGetters = useCartStore((state) => ({
		getById: state.getExtraProductById,
		getByIdWithLimit: state.getExtraProductByIdWithQuantityLimit,
		getChipCount: state.getExtraProductChipCount,
		getInitialChips: state.getExtraProductInitialChips,
		getState: state.getExtraProductStateById,
		isConfigRequired: state.isExtraProductConfigurationRequired,
	}));

	/** extra product groups */
	const groupGetters = useCartStore((state) => ({
		getById: state.getExtraProductGroupById,
		getProducts: state.getExtraProductGroupProducts,
		getPriceRange: state.getExtraProductGroupPriceRange,
		getState: state.getExtraProductGroupStateById,
	}));

	/** controls */
	const controls = useCartStore((state) => ({
		getSimple: state.getSimpleProductControls,
		getGroup: state.getProductGroupControls,
		getAll: state.getProductControls,
	}));

	/** cart ops */
	const cartOps = useCartStore((state) => ({
		add: state.addExtraProductToCartedTicket,
		update: state.updateExtraProductInCartedTicket,
		remove: state.removeExtraProductFromCartedTicket,
		getChipAmount: state.getCartedExtraProductChipAmount,
		getPrice: state.getCartedExtraProductPrice,
		get: state.getCartedExtraProduct,
	}));

	return { sheetConfig, productGetters, groupGetters, controls, cartOps };
}

export function useCartedTicketForm(cartTicketId: UUID) {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, 'useCartedTicketForm');
	const { ticket, ticketType } = useCartStore((state) => state.getCartedTicket({ cartTicketId }), 'deep');
	const errors = useCartStore((state) => state.cartedTicketsFormErrors[cartTicketId] || {});
	const touchedFields = useCartStore((state) => state.cartedTicketsTouchedFields[cartTicketId]);
	const updateCartedTicketField = useCartStore((s) => s.updateCartedTicketField);
	const setCartedTicketFieldTouched = useCartStore((s) => s.setCartedTicketFieldTouched);
	const setFieldValue = React.useCallback(
		(
			field: FieldPath<CartStoreCartedTicketsSlice.CartedTicket>,
			value: FieldPathValue<CartStoreCartedTicketsSlice.CartedTicket, FieldPath<CartStoreCartedTicketsSlice.CartedTicket>>,
		) => {
			return updateCartedTicketField({ cartTicketId, field, value });
		},
		[cartTicketId, updateCartedTicketField],
	);
	const setFieldTouched = React.useCallback(
		(field: FieldPath<CartStoreCartedTicketsSlice.CartedTicket>) => {
			return setCartedTicketFieldTouched({ cartTicketId, field });
		},
		[cartTicketId, setCartedTicketFieldTouched],
	);
	const getFieldError = React.useCallback(
		(field: FieldPath<CartStoreCartedTicketsSlice.CartedTicket>) => {
			const fieldError = errors[field] ?? getObject(errors, field);
			if (!isDefined(fieldError)) return undefined;
			const errorMessage = isObject(fieldError) && 'message' in fieldError ? fieldError.message : fieldError;
			if (typeof errorMessage !== 'string') {
				return 'Unknown error';
			}
			if (errorMessage.startsWith('t:')) {
				/** extract error key and parameters from message like "t:error.key(123)" */
				const matches = errorMessage.match(/^t:([^(]+)(?:\((\d+)\))?$/);
				if (!matches) return errorMessage;
				/** destructure matches array and ensure errorKey exists */
				const [, errorKey, countParam] = matches;
				if (!errorKey) return errorMessage;
				/** convert count parameter to number if it exists */
				const count = countParam ? Number(countParam) : undefined;
				return t(errorKey, { count: count ?? 0 });
			}
			return errorMessage;
		},

		[errors, t],
	);
	const isFieldValid = React.useCallback(
		(field: FieldPath<CartStoreCartedTicketsSlice.CartedTicket>) => {
			const fieldError = getFieldError(field);
			if (isDefined(fieldError)) return false;
			/** check for any nested errors (e.g. we have error on `chips.0.value` but we check for `chips`) */
			const nestedFields = Object.keys(errors).filter((e) => e.startsWith(field));
			if (nestedFields.length > 0) {
				return nestedFields.every((nestedField) => !isDefined(getFieldError(nestedField as FieldPath<CartStoreCartedTicketsSlice.CartedTicket>)));
			}
			return true;
		},
		[errors, getFieldError],
	);
	const isFieldTouched = React.useCallback(
		(field: FieldPath<CartStoreCartedTicketsSlice.CartedTicket>) => {
			return touchedFields?.has(field) ?? false;
		},
		[touchedFields],
	);
	const getFieldValue = React.useCallback(
		<TFieldPath extends FieldPath<CartStoreCartedTicketsSlice.CartedTicket>>(field: TFieldPath) => {
			return getObject(ticket, field) as FieldPathValue<CartStoreCartedTicketsSlice.CartedTicket, TFieldPath>;
		},
		[ticket],
	);

	return {
		values: ticket,
		ticketType,
		errors,
		touchedFields,
		setFieldValue,
		setFieldTouched,
		getFieldError,
		getFieldValue,
		isFieldValid,
		isFieldTouched,
	};
}

/* -------------------------------------------------------------------------------------------------
 * Api Queries
 * TODO: probably move to separate hooks.ts file
 * -----------------------------------------------------------------------------------------------*/

type UsePromoCodesApiQuery = { codes: string[]; type: 'promo' | 'partner' | 'benefit' };
export function usePromoCodesApi() {
	const cartConfig = useCartStore((s) => s.cartConfig);
	const ticketTypesConfig = useCartStore((s) => s.ticketTypesConfig);
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, 'usePromoCodesApi');
	const router = useRouter();
	const urlSearchParams = useRouterParams();
	const { passApi } = useApiClient();
	const toast = useToast();

	/** promo / partner codes */
	const partnerLinkQuery = urlSearchParams.get('partnerCode')?.split(',') ?? urlSearchParams.get('ticket_type_filter')?.split(',') ?? [];
	const promoCodeQuery = urlSearchParams.get('promoCode')?.split(',') ?? [];
	const benefitCodeQuery = calcValue(() => {
		const benefitCodes = urlSearchParams.get('benefitCode')?.split(',') ?? [];
		const benefitCodeProvider = urlSearchParams.get('bcpid');
		if (benefitCodes.length === 0 || isEmpty(benefitCodeProvider)) return [];
		return benefitCodes.map((code) => `${benefitCodeProvider}_${code}`);
	});
	const promoUrlQuery = calcValue<UsePromoCodesApiQuery | null>(() => {
		if (promoCodeQuery.length > 0) return { codes: promoCodeQuery, type: 'promo' };
		if (benefitCodeQuery.length > 0) return { codes: benefitCodeQuery, type: 'benefit' };
		if (partnerLinkQuery.length > 0) return { codes: partnerLinkQuery, type: 'partner' };
		return null;
	});

	/** promo code API */
	const promoCodeApi = useMutation({
		mutationKey: [passApi.v2Event.findOneCoupon.id] as const,
		async mutationFn(params: { codes: string[]; type: 'promo' | 'partner' }) {
			const newTicketTypes: Array<CartStoreTicketTypesSlice.TicketTypeConfig> = [];
			const orgPartners: Array<NonNullable<CartStoreTicketTypesSlice.TicketTypeConfig['orgPartner']>> = [];
			const originalTicketTypeIdsToHide: Array<UUID> = [];

			for (const code of params.codes) {
				/** call API to get ticketTypes and orgPartner from the promo code applied */
				const { data } = await passApi.v2Event.findOneCoupon.request(
					{ eventId: cartConfig.eventId },
					{ language: languageEnumTickets.get(router.locale, 'code').languageId, code },
				);

				/** push new ticket types */
				newTicketTypes.push(
					...data.ticketTypes.reduce<Array<CartStoreTicketTypesSlice.TicketTypeConfig>>((acc, ticketType) => {
						const ticketTypeConfig = createTicketTypeConfig(
							{
								...ticketType,
								orgPartner: getOrgPartnerDataOverride({
									namePub: data.orgPartner.namePub,
									logoUrl: data.orgPartner.logoUrl,
									shortCode: code,
									benefitCode: code,
									orgPartnerBackgroundColor: data.orgPartner.orgPartnerBackgroundColor,
									orgPartnerForegroundColor: data.orgPartner.orgPartnerForegroundColor,
								}),
							},
							{
								eventId: cartConfig.eventId,
								isTicketHolderNameRequired: cartConfig.isTicketHolderNameRequired,
								isTicketEmptyChipsGenerated: cartConfig.isTicketEmptyChipsGenerated,
								orgPartnerShortCode: code,
							},
						);

						/** find if a originalTicketTypeId is already in the cart (and thus should be hidden by adding this new one) */
						const ticketTypeInCart = ticketTypesConfig.find((tt) => tt.ticketTypeId === ticketType.originalTicketTypeId);
						if (isDefined(ticketTypeInCart) && ticketTypeInCart.saleStatus === 'active') {
							originalTicketTypeIdsToHide.push(ticketTypeInCart.ticketTypeId);
							/** move the ended promo ticket type after the active original ticket type */
							ticketTypeConfig.displayOrder = ticketTypeInCart?.displayOrder ?? ticketType.displayOrder;
						}

						/** prevent adding any unwanted ticket types */
						// TODO: maybe allow and just handle non-salable ticket types in the cart
						if (ticketTypeConfig.saleStatus === 'not-started') return acc;
						return [...acc, ticketTypeConfig];
					}, []),
				);

				/** push new orgPartner */
				orgPartners.push({
					namePub: data.orgPartner.namePub,
					logoUrl: data.orgPartner.logoUrl,
					shortCode: code,
					orgPartnerBackgroundColor: data.orgPartner.orgPartnerBackgroundColor,
					orgPartnerForegroundColor: data.orgPartner.orgPartnerForegroundColor,
				});
			}

			return { orgPartners, newTicketTypes, originalTicketTypeIdsToHide };
		},
		onSuccess(data) {
			/** do not trigger warning if the promo code is already applied */
			const isAlreadyApplied = ticketTypesConfig.some((tt) => data.orgPartners.some((op) => op.shortCode === tt.orgPartner?.shortCode));
			if (isAlreadyApplied) {
				toast.success(t('promoCodeApi.alreadyApplied'));
				return;
			}

			/** no ticket types found */
			if (data.newTicketTypes.length === 0) {
				toast.warning(t('promoCodeApi.noTicketTypes'));
				return;
			}

			/** success for all given orgPartner codes applied */
			for (const orgPartner of data.orgPartners) {
				toast.success(
					<div className="flex items-start gap-2 text-sm font-[500] text-zinc-900">
						<StarHalfIcon className="size-6" />
						<span>{t('promoCodeApi.successfullyApplied', { orgPartnerName: orgPartner.namePub })}</span>
					</div>,
				);
			}
		},
		onError() {
			toast.error(t('promoCodeApi.notFound'));
		},
	});

	/** benefit codes */
	const benefitCodeApi = useMutation({
		mutationKey: [passApi.v2Event.checkDiscountCode.id] as const,
		async mutationFn(params: { codes: string[] }) {
			const newTicketTypes: Array<CartStoreTicketTypesSlice.TicketTypeConfig> = [];
			const orgPartners: Array<NonNullable<CartStoreTicketTypesSlice.TicketTypeConfig['orgPartner']>> = [];
			const originalTicketTypeIdsToHide: Array<UUID> = [];

			for (const code of params.codes) {
				/** call API to get ticketTypes from the benefit code applied */
				const { data } = await passApi.v2Event.checkDiscountCode.request(
					{ eventId: cartConfig.eventId, code },
					{ language: languageEnumTickets.get(router.locale, 'code').languageId },
				);

				/** check for benefitCode redemption to not re-redeem again */
				if (isEmpty(data.redeemedAt)) {
					/** push new ticket types */
					newTicketTypes.push(
						...data.ticketTypes.reduce<Array<CartStoreTicketTypesSlice.TicketTypeConfig>>((acc, ticketType) => {
							const ticketTypeConfig = createTicketTypeConfig(
								{
									...ticketType,
									orgPartner: getOrgPartnerDataOverride({
										namePub: data.orgPartner.namePub,
										logoUrl: data.orgPartner.logoUrl,
										shortCode: code,
										benefitCode: code,
										orgPartnerBackgroundColor: data.orgPartner.orgPartnerBackgroundColor,
										orgPartnerForegroundColor: data.orgPartner.orgPartnerForegroundColor,
									}),
								},
								{
									eventId: cartConfig.eventId,
									isTicketHolderNameRequired: cartConfig.isTicketHolderNameRequired,
									isTicketEmptyChipsGenerated: cartConfig.isTicketEmptyChipsGenerated,
									orgPartnerShortCode: code,
									orgPartnerBenefitCode: code,
								},
							);

							/** find if a originalTicketTypeId is already in the cart (and thus should be hidden by adding this new one) */
							const ticketTypeInCart = ticketTypesConfig.find((tt) => tt.ticketTypeId === ticketType.originalTicketTypeId);
							if (isDefined(ticketTypeInCart) && ticketTypeInCart.saleStatus === 'active') {
								originalTicketTypeIdsToHide.push(ticketTypeInCart.ticketTypeId);
								/** move the ended promo ticket type after the active original ticket type */
								ticketTypeConfig.displayOrder = ticketTypeInCart?.displayOrder ?? ticketType.displayOrder;
							}

							/** prevent adding any unwanted ticket types */
							// TODO: maybe allow and just handle non-salable ticket types in the cart
							if (ticketTypeConfig.saleStatus === 'not-started') return acc;
							return [...acc, ticketTypeConfig];
						}, []),
					);

					/** push new orgPartner */
					orgPartners.push({
						namePub: data.orgPartner.namePub,
						logoUrl: data.orgPartner.logoUrl,
						shortCode: code,
						benefitCode: code,
						orgPartnerBackgroundColor: data.orgPartner.orgPartnerBackgroundColor,
						orgPartnerForegroundColor: data.orgPartner.orgPartnerForegroundColor,
					});
				}
			}

			return { newTicketTypes, orgPartners, originalTicketTypeIdsToHide };
		},
		onSuccess(data) {
			/** do not trigger warning if the promo code is already applied */
			const isAlreadyApplied = ticketTypesConfig.some((tt) => data.orgPartners.some((op) => op.benefitCode === tt.orgPartner?.benefitCode));
			if (isAlreadyApplied) {
				toast.success(t('promoCodeApi.alreadyApplied'));
				return;
			}

			/** no ticket types found */
			if (data.newTicketTypes.length === 0) {
				toast.warning(t('promoCodeApi.noTicketTypes'));
				return;
			}

			/** success for all given orgPartner codes applied */
			for (const orgPartner of data.orgPartners) {
				toast.success(
					<div className="flex items-start gap-2 text-sm font-[500] text-zinc-900">
						<StarHalfIcon className="size-6" />
						<span>{t('promoCodeApi.successfullyApplied', { orgPartnerName: orgPartner.namePub })}</span>
					</div>,
				);
			}
		},
		onError() {
			toast.error(t('promoCodeApi.notFound'));
		},
	});

	return { promoCodeApi, benefitCodeApi, promoUrlQuery };
}

export const useTicketTypesApiQuery = () => {
	const { promoUrlQuery } = usePromoCodesApi();
	const cartConfig = useCartStore((s) => s.cartConfig);
	const router = useRouter();
	const queryKey = cartStoreQueryKeys.getTicketTypesApiQueryKey(cartConfig.eventId, router.locale, promoUrlQuery);
	const isLoading = useIsFetching({ queryKey }) > 0;
	const queryClient = useQueryClient();
	const hasData = React.useMemo(() => queryClient.getQueryData(queryKey) !== undefined, [queryClient, queryKey]);
	const refetch = React.useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);
	return { isLoading, hasData, refetch };
};

export function useTicketTypesApi() {
	const { promoCodeApi, promoUrlQuery, benefitCodeApi } = usePromoCodesApi();
	const cartConfig = useCartStore((s) => s.cartConfig);
	const cartState = useCartStore((s) => cartStoreSelectors.getCartState(s));
	const loadTicketTypesConfig = useCartStore((s) => s.loadTicketTypesConfig);
	const router = useRouter();
	const { passApi } = useApiClient();
	const isMounted = useIsMounted();

	/** ticket types API */
	const ticketTypesApi = useQuery({
		queryKey: cartStoreQueryKeys.getTicketTypesApiQueryKey(cartConfig.eventId, router.locale, promoUrlQuery),
		async queryFn({ queryKey }) {
			const [, params, promoCodesQuery] = queryKey;

			/** get ticket types from API */
			const res = await passApi.v2Event.getTicketTypesFromEventFull.request(
				{ eventId: params.eventId },
				{ filter: undefined, language: languageEnumTickets.get(router.locale, 'code').languageId },
			);

			/** map ticket types to the desired format */
			let ticketTypesConfig = res.data.map((ticketType) =>
				createTicketTypeConfig(
					{ ...ticketType, saleFrom: null, saleTo: null },
					{
						eventId: cartConfig.eventId,
						isTicketHolderNameRequired: cartConfig.isTicketHolderNameRequired,
						isTicketEmptyChipsGenerated: cartConfig.isTicketEmptyChipsGenerated,
					},
				),
			);

			/** update ticket types config with promo codes */
			if (isDefined(promoCodesQuery)) {
				try {
					/** get promo code ticket types from API */
					const promoRes =
						promoCodesQuery.type === 'benefit'
							? await benefitCodeApi.mutateAsync(promoCodesQuery)
							: await promoCodeApi.mutateAsync({
									type: promoCodesQuery.type,
									codes: promoCodesQuery.codes,
								});

					/** update ticket types config */
					ticketTypesConfig = [
						/** hide original ticket types from already existing list of ticket types config */
						...ticketTypesConfig.map((ticketType) => {
							/** hide original ticket types */
							const isToHide = promoRes.originalTicketTypeIdsToHide.includes(ticketType.ticketTypeId);
							if (isToHide) return { ...ticketType, __isListable: false };
							return ticketType;
						}),
						/** add new ticket types */
						...promoRes.newTicketTypes,
					];
				} catch (e) {
					noop();
				}
			}

			return { ticketTypesConfig };
		},
		enabled:
			/** to prevent double fetch before query params (with promoCode) are ready */
			isMounted &&
			/** enabled only when sale is active (prevent unnecessary calls) */
			cartState.isTicketSaleEnabled,
		refetchOnWindowFocus: false,
		/** 5 minutes stale time */
		staleTime: makeTime('5 minutes'),
	});

	React.useEffect(() => {
		if (ticketTypesApi.data) {
			/** load ticket types config to cart store */
			loadTicketTypesConfig({
				ticketTypes: ticketTypesApi.data.ticketTypesConfig,
				forceOverwrite: true,
				duplicatesStrategy: 'replace',
				/** auto-cart only if ticket types are not stale (to prevent auto-cart on stale data) */
				shouldAutoCart: !ticketTypesApi.isStale,
			});
		}
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [ticketTypesApi.data?.ticketTypesConfig]);

	return { ticketTypesApi };
}

export function useTicketImportApi(options: { validate: (ticketCode: string) => boolean; isPartner?: boolean }) {
	const queryClient = useQueryClient();
	const ticketTypesConfig = useCartStore((s) => s.ticketTypesConfig);
	const cartConfig = useCartStore((s) => s.cartConfig);
	const getCartedTicket = useCartStore((s) => s.getCartedTicket);
	const { passApi } = useApiClient();

	/** ticket code management */
	const [ticketCode, _setTicketCode] = React.useState('');
	const setTicketCode = React.useCallback(
		(value: string) => {
			_setTicketCode(value);
			/** invalidate queries */
			if (isEmpty(value)) {
				queryClient.removeQueries({ queryKey: cartStoreQueryKeys.getTicketImportApiQueryKey(cartConfig.eventId, value) });
			}
		},
		[cartConfig.eventId, queryClient],
	);
	const isTicketCodeValid = React.useMemo(() => {
		return !isEmpty(ticketCode) && options.validate(ticketCode);
	}, [ticketCode, options]);

	const ticketCodeMutation = useMutation({
		async mutationFn(params: { ticketCode: string }) {
			/** call API to get ticket by the ticket code */
			const res = await passApi.v2Event.findTicketByCode.request({ eventId: cartConfig.eventId, qrCode: params.ticketCode });

			const isWithinTicketTypes = ticketTypesConfig.some(
				(ticketType) => ticketType.ticketTypeId === res.data.ticketType.ticketTypeId && !ticketType.__isImported,
			);
			if (isWithinTicketTypes) throw new InternalError('ticket-type-not-available', 'Ticket type is not available for this event');

			/** check if ticket is already in the cart */
			const isDuplicate = isDefined(silenceError(() => getCartedTicket({ cartTicketId: res.data.ticket.ticketId }).ticket, null));

			/** get ticket type config */
			const ticketTypeConfig = createTicketTypeConfig(
				{ ...res.data.ticketType, saleFrom: null, saleTo: null },
				{
					eventId: cartConfig.eventId,
					isTicketEmptyChipsGenerated: cartConfig.isTicketEmptyChipsGenerated,
					isTicketHolderNameRequired: cartConfig.isTicketHolderNameRequired,
				},
			);
			ticketTypeConfig.__isImported = true;
			ticketTypeConfig.__isListable = false;

			return {
				ticketType: ticketTypeConfig,
				isDuplicate,
				isTopupable: res.data.topupable && res.data.ticket.chips.length > 0,
				ticket: {
					ticketId: res.data.ticket.ticketId,
					/** fallback to undefined (prevent null values) */
					holderName: res.data.ticket.name ?? undefined,
					email: res.data.ticket.email ?? undefined,
					chips: res.data.ticket.chips,
				} satisfies CartStoreCartedTicketsSlice.ImportedTicket,
			};
		},
	});

	/** ticket code API query */
	const ticketCodeApi = useQuery({
		queryKey: cartStoreQueryKeys.getTicketImportApiQueryKey(cartConfig.eventId, ticketCode),
		async queryFn({ queryKey }) {
			const [, params] = queryKey;
			return ticketCodeMutation.mutateAsync({ ticketCode: params.ticketCode });
		},
		enabled: isTicketCodeValid,
		retry: false,
		refetchOnWindowFocus: false,
	});

	return { ticketCode, setTicketCode, isTicketCodeValid, ticketCodeApi, ticketCodeMutation };
}

export function useExtraProductsApi() {
	const router = useRouter();
	const cartConfig = useCartStore((s) => s.cartConfig);
	const cartedTicketTypeIds = useCartStore((s) => s.getCartedTicketTypes().map((tt) => tt.ticketTypeId));
	const currentCartView = useCartStore((s) => s.currentCartView);
	const loadExtraProductsConfig = useCartStore((s) => s.loadExtraProductsConfig);
	const loadExtraProductGroupsConfig = useCartStore((s) => s.loadExtraProductGroupsConfig);
	const { passApi } = useApiClient();

	const extraProductsApi = useQuery({
		queryKey: cartStoreQueryKeys.getExtraProductsApiQueryKey(cartConfig.eventId, router.locale, cartedTicketTypeIds),
		async queryFn({ queryKey }) {
			const [, params] = queryKey;

			/** get extra products and groups from API */
			const [extraProductsRes, groupsRes] = await Promise.all([
				passApi.v2Ticket.findExtraProductsFromTicketType.request(params),
				passApi.v2Ticket.findGroupsFromTicketType.request(params),
			]);

			/** create extra products config */
			const extraProductsConfig = extraProductsRes.data.map((extraProduct) => {
				return createExtraProductConfig(extraProduct, { eventId: cartConfig.eventId });
			});

			/** load extra products to cart store */
			loadExtraProductsConfig({ extraProducts: extraProductsConfig, duplicatesStrategy: 'replace', forceOverwrite: true });

			/** create extra product groups config */
			const extraProductGroupsConfig = groupsRes.data.map((group) => {
				return createExtraProductGroupConfig(group, { eventId: cartConfig.eventId });
			});

			/** load extra product groups to cart store */
			loadExtraProductGroupsConfig({ extraProductGroups: extraProductGroupsConfig, duplicatesStrategy: 'replace', forceOverwrite: true });

			return { extraProductsConfig, extraProductGroupsConfig };
		},
		refetchOnWindowFocus: false,
		enabled: currentCartView === CART_VIEW.PRODUCTS,
	});

	return { extraProductsApi };
}

export function useIsCartProcessing() {
	const cartConfig = useCartStore((s) => s.cartConfig);
	const openedSectorId = useCartStore((s) => s.openedSectorId);
	const cartedTicketTypeIds = useCartStore((s) => s.getCartedTicketTypes().map((tt) => tt.ticketTypeId));
	const router = useRouter();
	const queryClient = useQueryClient();

	const extraProductsApiQueryKey = cartStoreQueryKeys.getExtraProductsApiQueryKey(cartConfig.eventId, router.locale, cartedTicketTypeIds);
	const extraProductsQueryFetching = queryClient.isFetching({ queryKey: extraProductsApiQueryKey }) > 0;
	const isExtraProductsLoading = React.useMemo(() => {
		const extraProductsRes = queryClient.getQueryData(extraProductsApiQueryKey);
		return !isDefined(extraProductsRes) && extraProductsQueryFetching;
	}, [queryClient, extraProductsApiQueryKey, extraProductsQueryFetching]);

	const venueApiQueryKey = cartStoreQueryKeys.getVenueApiQueryKey(cartConfig.eventId, router.locale);
	const venueQueryFetching = queryClient.isFetching({ queryKey: venueApiQueryKey }) > 0;
	const sectorApiQueryKey = cartStoreQueryKeys.getSectorApiQueryKey(openedSectorId ?? '', router.locale);
	const sectorQueryFetching = queryClient.isFetching({ queryKey: sectorApiQueryKey }) > 0;
	const seatingLoading = React.useMemo(() => {
		const venueRes = queryClient.getQueryData(venueApiQueryKey);
		const venueLoading = !isDefined(venueRes) && venueQueryFetching;
		const sectorRes = queryClient.getQueryData(sectorApiQueryKey);
		const sectorLoading = !isDefined(sectorRes) && sectorQueryFetching;

		return { sectorLoading, venueLoading };
	}, [queryClient, sectorApiQueryKey, sectorQueryFetching, venueApiQueryKey, venueQueryFetching]);

	const { createOrderApi } = useCartOrderApi({ onOrderCreated: noop });

	return {
		isExtraProductsLoading,
		isSeatingLoading: seatingLoading.sectorLoading || seatingLoading.venueLoading,
		isSeatinSectorLoading: seatingLoading.sectorLoading,
		isSeatingVenueLoading: seatingLoading.venueLoading,
		isOrderProcessing: createOrderApi.isPending,
		isProcessing: isExtraProductsLoading,
	};
}

export async function venueApiQueryFn(params: { eventId: UUID; passApi: PassApi<any> }) {
	const { eventId, passApi } = params;
	/** get venue with sectors from API */
	const res = await passApi.v2Event.findOneVenueWithSectors.request({ eventId });
	const sectors = res.data.sectors.map((sector) => {
		return { ...sector, crowdCount: sector.totalCrowdCount, seatCount: sector.totalSeatCount };
	});
	return { venue: res.data, sectors };
}

export async function categoriesApiQueryFn(params: { eventId: UUID; passApi: PassApi<any> }) {
	const { eventId, passApi } = params;
	/** get ticket categories from API */
	const res = await passApi.v2Event.findAllReservationCategoriesFromEvent.request({ eventId });
	const ticketCategoriesConfig = res.data.map((category): CartStoreSeatingSlice.CategoryConfig => {
		return {
			...category,
			color: hexAlpha(category.color),
			/** this requires access to cartStore and is populated in useQuery implementation */
			ticketTypesAvailable: 0,
		};
	});
	return { ticketCategoriesConfig };
}

export function useSeatingApi() {
	const router = useRouter();
	const cartConfig = useCartStore((s) => s.cartConfig);
	const { passApi } = useApiClient();

	const loadVenueConfig = useCartStore((s) => s.loadVenueConfig);
	const loadSectorsConfig = useCartStore((s) => s.loadSectorsConfig);
	const openedSectorId = useCartStore((s) => s.openedSectorId);
	const openSector = useCartStore((s) => s.openSector);
	const getCartedTicketsFromCrowd = useCartStore((s) => s.getCartedTicketsFromCrowd);
	const loadSeatsConfig = useCartStore((s) => s.loadSeatsConfig);
	const loadCrowdsConfig = useCartStore((s) => s.loadCrowdsConfig);
	const getCartedTicketFromSeat = useCartStore((s) => s.getCartedTicketFromSeat);
	const getCategoryTicketTypes = useCartStore((s) => s.getCategoryTicketTypes);
	const hasReservableTicketTypes = useCartStore((s) => s.ticketTypesConfig.some((tt) => tt.isReservable));

	/** venue API query */
	const venueApi = useQuery({
		queryKey: cartStoreQueryKeys.getVenueApiQueryKey(cartConfig.eventId, router.locale),
		async queryFn({ queryKey }) {
			const [, params] = queryKey;
			return venueApiQueryFn({ eventId: params.eventId, passApi });
		},
		refetchOnWindowFocus: false,
		/** 5 minutes stale time */
		staleTime: makeTime('5 minutes'),
		/** enable immediately with presence of ticket types with reservation */
		enabled: hasReservableTicketTypes,
	});

	/** load venue config to cart store */
	React.useEffect(() => {
		if (isDefined(venueApi.data)) {
			loadVenueConfig({ venue: { ...venueApi.data.venue, sectors: venueApi.data.sectors } });
			/** load initial sectors from venue data */
			loadSectorsConfig({ sectors: venueApi.data.sectors, forceOverwrite: false, duplicatesStrategy: 'ignore' });
			/** auto-open first and only sector */
			if (venueApi.data.sectors.length === 1) openSector({ sectorId: venueApi.data.sectors[0]!.sectorId });
		}
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [venueApi.data?.venue, venueApi.data?.sectors]);

	/** sector API query handler */
	const sectorApi = useQuery({
		queryKey: cartStoreQueryKeys.getSectorApiQueryKey(openedSectorId ?? '', router.locale),
		async queryFn({ queryKey }) {
			const [, params] = queryKey;

			/** get sector from API */
			const res = await passApi.v2Sector.findOne.request({ sectorId: params.sectorId });

			/** process crowds */
			const newCrowds = res.data.crowds.map((crowd) => {
				const foundInTickets = getCartedTicketsFromCrowd({ crowdId: crowd.crowdId });
				const ticketTypes = getCategoryTicketTypes({ categoryId: crowd.categoryId });
				return {
					...crowd,
					sectorId: params.sectorId,
					ticketTypesAvailable: ticketTypes.length,
					reservationCrowdId: foundInTickets[0]?.reservation.crowd?.reservationCrowdId ?? null,
				};
			});
			loadCrowdsConfig({ crowds: newCrowds, forceOverwrite: false, duplicatesStrategy: 'replace' });

			/** process seats */
			const newSeats = res.data.seats.map((seat) => {
				const foundInTickets = getCartedTicketFromSeat({ seatId: seat.seatId });
				const ticketTypes = getCategoryTicketTypes({ categoryId: seat.categoryId });
				return {
					...seat,
					sectorId: params.sectorId,
					ticketTypesAvailable: ticketTypes.length,
					reservationSeatId: foundInTickets?.reservation.seat?.reservationSeatId ?? null,
				};
			});
			loadSeatsConfig({ seats: newSeats, forceOverwrite: false, duplicatesStrategy: 'replace' });

			return { sector: res.data, crowds: newCrowds, seats: newSeats };
		},
		refetchOnWindowFocus: false,
		/** 5 minutes stale time */
		staleTime: makeTime('5 minutes'),
		enabled: isDefined(openedSectorId),
	});

	return {
		sectorApi,
		venueApi,
	};
}

export function useSeatingCategoriesApi() {
	const router = useRouter();
	const cartConfig = useCartStore((s) => s.cartConfig);
	const { passApi } = useApiClient();
	const hasReservableTicketTypes = useCartStore((s) => s.ticketTypesConfig.some((tt) => tt.isReservable));
	const getCategoryTicketTypes = useCartStore((s) => s.getCategoryTicketTypes);
	const loadCategoriesConfig = useCartStore((s) => s.loadCategoriesConfig);

	/** fetch event reservation categories */
	const categoriesApi = useQuery({
		queryKey: cartStoreQueryKeys.getCategoriesApiQueryKey(cartConfig.eventId, router.locale),
		queryFn: async ({ queryKey }) => {
			const [, params] = queryKey;
			const res = await categoriesApiQueryFn({ eventId: params.eventId, passApi });
			return {
				ticketCategoriesConfig: res.ticketCategoriesConfig.map((category) => ({
					...category,
					color: hexAlpha(category.color),
					ticketTypesAvailable: getCategoryTicketTypes({ categoryId: category.categoryId }).length,
				})),
			};
		},
		refetchOnWindowFocus: false,
		/** 5 minutes stale time */
		staleTime: makeTime('5 minutes'),
		enabled:
			/** enable only for events with reservationVenue (meaning seating active) */
			/** and only when tickets sale is active (transitive from ticket types API) */
			hasReservableTicketTypes,
	});

	/** load ticket categories to cart store */
	React.useEffect(() => {
		if (isDefined(categoriesApi.data)) {
			loadCategoriesConfig({ categories: categoriesApi.data.ticketCategoriesConfig, forceOverwrite: true, duplicatesStrategy: 'replace' });
		}
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [categoriesApi.data?.ticketCategoriesConfig]);

	return { categoriesApi };
}

export function useSeatingMutations() {
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const { trackingModule } = useTracking();
	const cartConfig = useCartStore((s) => s.cartConfig);
	const { passApi } = useApiClient();
	const router = useRouter();
	const queryClient = useQueryClient();
	const openedSectorId = useCartStore((s) => s.openedSectorId);

	/** reservation mutations */
	const seatingReservation = useCartStore((s) => s.seatingReservation);
	const createSeatingReservation = useCartStore((s) => s.createSeatingReservation);
	const createReservationMutation = useMutation({
		async mutationFn() {
			log.debug('creating a new reservation');

			/** check if there is already a reservation */
			if (isDefined(seatingReservation)) {
				throw new InvalidStateError('Cannot create a new reservation because there is already a reservation');
			}

			/** call API to create a reservation */
			const { data } = await passApi.reservation.create.request({ eventId: cartConfig.eventId });

			/** format reservation data */
			const newReservation = {
				reservationId: data.reservationId,
				created: new Date(data.created),
				reservedUntil: !isEmpty(data.reservedUntil) ? new Date(data.reservedUntil) : undefined,
				crowds: [],
				seats: [],
			} satisfies CartStoreSeatingSlice.Reservation;

			return newReservation;
		},
		onSuccess(newReservation) {
			createSeatingReservation({ reservation: newReservation });
			log.debug(
				`Reservation "${newReservation.reservationId}" created, it will expire ${
					isDefined(newReservation.reservedUntil) ? `in ${formatDistanceToNow(newReservation.reservedUntil, { includeSeconds: true })}` : 'never'
				}`,
			);
		},
		onError(error: ApiError) {
			log.error('Error creating a reservation', error);
			/** capture error to Sentry */
			Sentry.captureException(error, { tags: { hook: 'useSeatingApi', method: 'createReservationMutation' } });
			/** open reservation error modal */
			openModal('reservationError', {
				centered: true,
				closeOnClickOutside: false,
				closeOnEscape: false,
				withCloseButton: false,
				title: t(`${ReservationErrorModal.displayName}.title`),
				innerProps: {
					error,
					onButtonClick: async ({ closeModal, id }: { closeModal: (id: string) => void; id: string }) => {
						/** close the modal */
						closeModal(id);
					},
				},
			});
		},
	});

	const clearSeatingReservation = useCartStore((s) => s.clearSeatingReservation);
	const cancelReservationMutation = useMutation({
		async mutationFn() {
			log.debug('cancelling reservation');

			/** check if there is a reservation */
			if (!isDefined(seatingReservation)) {
				throw new Error('Cannot cancel reservation because there is no reservation');
			}

			/** call API to cancel reservation */
			await passApi.reservation.cancel.request({ reservationId: seatingReservation.reservationId });

			return seatingReservation;
		},
		onMutate() {
			/** clear reservation state before API call */
			clearSeatingReservation();
		},
		onError(error: ApiError) {
			log.error('Error cancelling reservation', error);
			/** capture error to Sentry */
			Sentry.captureException(error, { tags: { hook: 'useSeatingApi', method: 'cancelReservationMutation' } });
			/** clear reservation state on error anyway */
			clearSeatingReservation();
		},
	});

	const addCartedTicket = useCartStore((s) => s.addCartedTicket);
	const unselectSeat = useCartStore((s) => s.unselectSeat);
	const addSeatToSeatingReservation = useCartStore((s) => s.addSeatToSeatingReservation);

	/** create seat reservation mutation */
	const createSeatReservationMutation = useMutation({
		mutationKey: ['createSeatReservationMutation'],
		async mutationFn(params: { seatId: UUID; ticketTypeId: UUID }) {
			log.debug(`Reserving seat "${params.seatId}"`);

			let reservation = seatingReservation;
			if (!isDefined(reservation)) {
				/** create a new reservation */
				const res = await createReservationMutation.mutateAsync();
				if (!isDefined(res.reservationId)) throw new InvalidStateError('Reservation could not be created');
				reservation = res;
			}

			/** check for opened sector */
			if (!isDefined(openedSectorId))
				throw new InternalError('use_cart_seating_error', 'Cannot update reservation because there is no opened sector');

			/** call API to create a seat reservation */
			const { data } = await passApi.reservation.createReservationSeat.request({ reservationId: reservation.reservationId, seatId: params.seatId });

			const newReservedSeat = { reservationSeatId: data.seatId, seatId: data.seatId } satisfies CartStoreSeatingSlice.ReservationSeat;

			/** add seat to reservation and update seats */
			addSeatToSeatingReservation(newReservedSeat);

			/** add new ticket for ticket type provided */
			addCartedTicket({
				input: { ticketTypeId: params.ticketTypeId, holderName: '', reservation: { crowd: null, seat: newReservedSeat } },
				trackingModule,
			});

			return newReservedSeat;
		},
		onSuccess() {
			/** unselect the seat to close the sheet */
			unselectSeat();
		},
		onError(error: ApiError) {
			log.error('Error reserving seat', error);
			/** capture error to Sentry */
			Sentry.captureException(error, { tags: { hook: 'useSeatingApi', method: 'createSeatReservationMutation' } });
		},
	});

	/** create crowd reservation mutation */
	const addCrowdToSeatingReservation = useCartStore((s) => s.addCrowdToSeatingReservation);
	const createCrowdReservationMutation = useMutation({
		async mutationFn(params: { crowdId: UUID; ticketTypeId: UUID }) {
			log.debug(`Reserving crowd "${params.crowdId}"`);

			let reservation = seatingReservation;
			if (!isDefined(reservation)) {
				/** create a new reservation */
				const res = await createReservationMutation.mutateAsync();
				if (!isDefined(res.reservationId)) throw new InvalidStateError('Reservation could not be created');
				reservation = res;
			}

			/** check for opened sector */
			if (!isDefined(openedSectorId))
				throw new InternalError('use_cart_seating_error', 'Cannot update reservation because there is no opened sector');

			/** call API to create a crowd reservation */
			const { data } = await passApi.reservation.createReservationCrowd.request({
				reservationId: reservation.reservationId,
				crowdId: params.crowdId,
			});

			const newReservedCrowd = {
				reservationCrowdId: data.reservationCrowdId,
				crowdId: params.crowdId,
			} satisfies CartStoreSeatingSlice.ReservationCrowd;

			/** add crowd to reservation and update crowds */
			addCrowdToSeatingReservation(newReservedCrowd);

			/** add new ticket for ticket type provided */
			addCartedTicket({
				input: { ticketTypeId: params.ticketTypeId, holderName: '', reservation: { crowd: newReservedCrowd, seat: null } },
				trackingModule,
			});

			return newReservedCrowd;
		},
		onSuccess() {
			/** unselect the seat to close the sheet */
			unselectSeat();
		},
		onError(error: ApiError) {
			log.error('Error reserving crowd', error);
			/** capture error to Sentry */
			Sentry.captureException(error, { tags: { hook: 'useSeatingApi', method: 'createCrowdReservationMutation' } });

			try {
				/** try to refresh the sector API (both seats and crowds) */
				queryClient.invalidateQueries({ queryKey: cartStoreQueryKeys.getSectorApiQueryKey(openedSectorId ?? '', router.locale) });
			} catch (e) {
				/** open reservation error modal */
				openModal('reservationError', {
					centered: true,
					closeOnClickOutside: false,
					closeOnEscape: false,
					withCloseButton: false,
					title: t(`${ReservationErrorModal.displayName}.title`),
					innerProps: {
						error,
						onButtonClick: async ({ closeModal, id }) => {
							/** close the modal */
							closeModal(id);
						},
					},
				});
			}
		},
	});

	return {
		createReservationMutation,
		cancelReservationMutation,
		createSeatReservationMutation,
		createCrowdReservationMutation,
	};
}

export function useCartOrderApi(options: {
	onOrderCreated: (params: { orderId: UUID; amount: number; enforcePaymentMethod: boolean }) => Promise<void> | void;
}) {
	const format = useFormat();
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const { trackingModule } = useTracking();
	const { passApi } = useApiClient();

	const cartedTickets = useCartStore((s) => s.cartedTickets);
	const maximusCartedProducts = useCartStore((s) => s.maximusCartedProducts);
	const activePaymentOptionId = useCartStore((s) => s.activePaymentOptionId);
	const paymentOptionsConfig = useCartStore((s) => s.paymentOptionsConfig);
	const isPaymentOptionEnforced = useCartStore((s) => {
		/** payment option (method) is enforced if any carted ticket type has promotionCampaign with restricted payment method */
		return s.cartedTickets.some((ticket) => {
			const ticketType = s.getTicketTypeById({ ticketTypeId: ticket.ticketTypeId });
			if (!isDefined(ticketType.extraData.promotionCampaign)) return false;
			const activePaymentOption = s.getActivePaymentOption();
			if (!isDefined(activePaymentOption)) return false;
			return ticketType.extraData.promotionCampaign.allowedPaymentMethods?.includes(activePaymentOption.paymentMethodId) ?? false;
		});
	});
	const cartConfig = useCartStore((s) => s.cartConfig);
	const contactDetails = useCartStore((s) => s.contactDetails);
	const paymentOptionsListed = useCartStore((s) => cartStoreSelectors.listPaymentOptions(s));
	const seatingReservation = useCartStore((s) => s.seatingReservation);
	const hasImportedTicketCarted = useCartStore((store) => cartStoreSelectors.hasSomeImportedCartedTicket(store));
	const hasReservableTicketTypes = useCartStore((s) =>
		s.cartedTickets.some((ticket) => s.getTicketTypeById({ ticketTypeId: ticket.ticketTypeId }).isReservable),
	);

	const clearSeatingReservation = useCartStore((s) => s.clearSeatingReservation);
	const getTicketTypeById = useCartStore((s) => s.getTicketTypeById);
	const getExtraProductById = useCartStore((s) => s.getExtraProductById);
	const getCartedTicketHolderNameFallback = useCartStore((s) => s.getCartedTicketHolderNameFallback);
	const getCartTotal = useCartStore((s) => s.getCartTotal);
	const setCartView = useCartStore((s) => s.setCartView);

	/** returns whether the order can be created */
	const [canCreateOrder, unableToCreateOrderReason] = React.useMemo(() => {
		/** can't create order with no carted tickets */
		if (cartedTickets.length === 0) return [false, 'no_carted_tickets'];
		/** can't create order with no payment method */
		if (!isDefined(activePaymentOptionId)) return [false, 'no_payment_method'];
		/** invalid payment method selected */
		if (!paymentOptionsListed.some((pm) => pm.paymentOptionId === activePaymentOptionId)) return [false, 'invalid_payment_option'];
		/** can't create order with no contact details */
		const parsedContactDetailsRes = contactDetailsSchema.safeParse(contactDetails);
		if (!parsedContactDetailsRes.success) return [false, parsedContactDetailsRes.error.message ?? 'no_contact_details'];
		/** can't create order with reservable tickets without reservation*/
		if (hasReservableTicketTypes && !isDefined(seatingReservation)) return [false, 'no_seating_reservation'];
		/** can't create order in demo mode */
		if (cartConfig.isDemoCart) return [false, 'demo_mode'];
		/** can create order */
		return [true, null];
	}, [
		activePaymentOptionId,
		cartConfig.isDemoCart,
		cartedTickets.length,
		contactDetails,
		hasReservableTicketTypes,
		paymentOptionsListed,
		seatingReservation,
	]);

	/** executes normal order flow */
	const executeOrderNormal = React.useCallback(async () => {
		/** check for ability to create order */
		if (!canCreateOrder) throw new Error(`[useCart] Cannot create order: ${unableToCreateOrderReason}`);

		const cartTotal = getCartTotal();
		const paymentOptionConfig = paymentOptionsConfig.find((pm) => pm.paymentOptionId === activePaymentOptionId);
		/** create the order */
		const order = cleanObject<OrderCreateV2BodyDto>({
			reservationId: seatingReservation?.reservationId,
			eventId: cartConfig.eventId,
			paymentMethodId: valOrThrow(paymentOptionConfig?.paymentMethodId, 'Cannot create order without an active payment method'),
			/** bank transfer override */
			...(paymentOptionConfig?.paymentMethodId === PAYMENT_METHOD.BANK_TRANSFER && {
				bankAccountId: valOrThrow(
					paymentOptionConfig.bankAccount?.bankAccountId,
					'Cannot create order with bank transfer without selected bank account',
				),
			}),
			/** override paymentMethod when cart total is 0 */
			...(cartTotal.total === 0 && { paymentMethodId: PAYMENT_METHOD.CREDIT_CARD, bankAccountId: undefined }),
			/** benefit codes */
			benefitCodes: cartedTickets.reduce<string[]>((acc, ticket) => {
				const ticketType = getTicketTypeById({ ticketTypeId: ticket.ticketTypeId });
				if (isDefined(ticketType.orgPartner) && !isEmpty(ticketType.orgPartner.benefitCode)) acc.push(ticketType.orgPartner.benefitCode);
				return acc;
			}, []),
			/** event tickets */
			tickets: cartedTickets.map((ticket) => ({
				_ticketId: ticket.cartTicketId,
				ticketTypeId: ticket.ticketTypeId,
				email: contactDetails.email,
				name: isEmpty(ticket.holderName) ? getCartedTicketHolderNameFallback(ticket) : ticket.holderName,
				userData: ticket.userData,
				seatId: ticket.reservation.seat?.seatId ?? undefined,
				reservationCrowdId: ticket.reservation.crowd?.reservationCrowdId ?? undefined,
				extraProducts: ticket.extraProducts.map((extraProduct) => ({
					ticketProductId: extraProduct.ticketProductId,
					count: extraProduct.count,
					optionalText: !isEmpty(extraProduct.optionalText)
						? extraProduct.optionalText
						: isDefined(extraProduct.optionalTextShippingAddress)
							? [
									extraProduct.optionalTextShippingAddress.address,
									extraProduct.optionalTextShippingAddress.city,
									extraProduct.optionalTextShippingAddress.postalCode,
									extraProduct.optionalTextShippingAddress.country,
								].join(', ')
							: undefined,
					chips: arrayOf(extraProduct.count).reduce<OrderCreateV2BodyDto['tickets'][number]['chips']>(
						(acc) => [...acc, ...extraProduct.chips.map((chip) => ({ productItemId: chip.productItemId, amount: chip.amount }))],
						[],
					),
				})),
				chips: ticket.chips.map((chip) => ({ productItemId: chip.productItemId, amount: chip.amount })),
			})),
			/** extra order contents (maximus) */
			extraOrderContents: maximusCartedProducts.map((maximusProduct) => {
				return {
					productId: maximusProduct.maximusProductId,
					count: maximusProduct.count,
					ticketIds: isEmpty(maximusProduct.cartTicketId) ? [] : [maximusProduct.cartTicketId],
				};
			}),
			/** account */
			account: {
				email: contactDetails.email,
				firstName: contactDetails.firstName,
				lastName: contactDetails.lastName,
				phone: contactDetails.phone ?? undefined,
				address: contactDetails.address ?? undefined,
				city: contactDetails.city ?? undefined,
				postalCode: contactDetails.postalCode ?? undefined,
				regNumber: contactDetails.regNumber ?? undefined,
				vatId: contactDetails.vatId ?? undefined,
				legalName: contactDetails.legalName ?? undefined,
				countryId: contactDetails.countryId ?? undefined,
				languageId: format.language.languageId,
			},
			/** meta */
			reqSource: cartConfig.reqSourceId,
			reqVersion: packageJson.version,
		});

		/** call API to create an order */
		const res = await passApi.order.orderCreateV2.request(order);

		/** return the order */
		return { orderId: res.data.orderId, ...order, amount: cartTotal.total };
	}, [
		canCreateOrder,
		unableToCreateOrderReason,
		getCartTotal,
		paymentOptionsConfig,
		seatingReservation?.reservationId,
		cartConfig.eventId,
		cartConfig.reqSourceId,
		cartedTickets,
		maximusCartedProducts,
		contactDetails.email,
		contactDetails.firstName,
		contactDetails.lastName,
		contactDetails.phone,
		contactDetails.address,
		contactDetails.city,
		contactDetails.postalCode,
		contactDetails.regNumber,
		contactDetails.vatId,
		contactDetails.legalName,
		contactDetails.countryId,
		format.language.languageId,
		passApi.order.orderCreateV2,
		activePaymentOptionId,
		getTicketTypeById,
		getCartedTicketHolderNameFallback,
	]);

	/** executes order flow with imported tickets */
	const executeOrderWithImport = React.useCallback(async () => {
		/** check for ability to create order */
		if (!canCreateOrder) throw new Error(`[useCart] Cannot create order: ${unableToCreateOrderReason}`);

		const cartTotal = getCartTotal();
		const paymentOptionConfig = paymentOptionsConfig.find((pm) => pm.paymentOptionId === activePaymentOptionId);

		const order = cleanObject<OrderCreateCreditFromCodeBodyDto>({
			eventId: cartConfig.eventId,
			paymentMethodId: valOrThrow(paymentOptionConfig?.paymentMethodId, 'Cannot create order without an active payment method'),
			/** bank transfer override */
			...(paymentOptionConfig?.paymentMethodId === PAYMENT_METHOD.BANK_TRANSFER && {
				bankAccountId: valOrThrow(
					paymentOptionConfig.bankAccount?.bankAccountId,
					'Cannot create order with bank transfer without selected bank account',
				),
			}),
			/** override paymentMethod when cart total is 0 */
			...(cartTotal.total === 0 && { paymentMethodId: PAYMENT_METHOD.CREDIT_CARD, bankAccountId: undefined }),
			tickets: cartedTickets.map((ticket) => ({
				ticketId: ticket.cartTicketId,
				email: contactDetails.email,
				name: isEmpty(ticket.holderName) ? getCartedTicketHolderNameFallback(ticket) : ticket.holderName,
				userData: ticket.userData,
				chips: ticket.chips.map((chip) => ({ chipId: chip.cartChipId, amount: chip.amount })),
			})),
			account: {
				email: contactDetails.email,
				firstName: contactDetails.firstName,
				lastName: contactDetails.lastName,
				phone: contactDetails.phone ?? undefined,
				address: contactDetails.address ?? undefined,
				city: contactDetails.city ?? undefined,
				postalCode: contactDetails.postalCode ?? undefined,
				regNumber: contactDetails.regNumber ?? undefined,
				vatId: contactDetails.vatId ?? undefined,
				legalName: contactDetails.legalName ?? undefined,
				countryId: contactDetails.countryId ?? undefined,
				languageId: format.language.languageId,
			},
			reqSource: cartConfig.reqSourceId,
			reqVersion: packageJson.version,
		});

		/** call API to credit order for the tickets */
		const res = await passApi.v2Order.orderCreditFromCode.request(order);

		/** return the order */
		return { orderId: res.data.orderId, ...order, amount: cartTotal.total };
	}, [
		canCreateOrder,
		unableToCreateOrderReason,
		getCartTotal,
		paymentOptionsConfig,
		cartConfig.eventId,
		cartConfig.reqSourceId,
		cartedTickets,
		contactDetails.email,
		contactDetails.firstName,
		contactDetails.lastName,
		contactDetails.phone,
		contactDetails.address,
		contactDetails.city,
		contactDetails.postalCode,
		contactDetails.regNumber,
		contactDetails.vatId,
		contactDetails.legalName,
		contactDetails.countryId,
		format.language.languageId,
		passApi.v2Order.orderCreditFromCode,
		activePaymentOptionId,
		getCartedTicketHolderNameFallback,
	]);

	/** order create API handler */
	const createOrderApi = useMutation({
		async mutationFn() {
			/** execute the order and get its output data */
			const orderData = hasImportedTicketCarted ? await executeOrderWithImport() : await executeOrderNormal();

			/** handle purchase tracking event */
			trackingModule.safeTracking(() =>
				trackingModule.purchase(
					{
						value: orderData.amount,
						currencyId: cartConfig.currencyId,
						tickets: cartedTickets.map((ticket) => ({
							cartTicketId: ticket.cartTicketId,
							chips: ticket.chips,
							ticketType: getTicketTypeById({ ticketTypeId: ticket.ticketTypeId }),
							reservation: ticket.reservation,
							holderName: ticket.holderName,
							userData: ticket.userData,
							extraProducts: ticket.extraProducts.map((ep) => {
								const extraProductConfig = getExtraProductById({ ticketProductId: ep.ticketProductId });
								return {
									ticketProductId: ep.ticketProductId,
									name: extraProductConfig.name,
									price: extraProductConfig.price,
									displayOrder: extraProductConfig.displayOrder,
									count: ep.count,
									chips: ep.chips,
								};
							}),
							trackingContext: cartConfig.eventContext,
						})),
						orderId: orderData.orderId,
					},
					{ eventId: cartConfig.eventId, orderId: orderData.orderId },
				),
			);

			/** call onOrderCreated callback */
			options.onOrderCreated({ orderId: orderData.orderId, amount: orderData.amount, enforcePaymentMethod: isPaymentOptionEnforced });

			/** return order data */
			return orderData;
		},
		async onError(error: ApiError) {
			/** capture error to Sentry */
			Sentry.captureException(error, { tags: { hook: 'useCart', method: 'createOrderHandler' } });
			/** for reservation-based orders, clean up the reservation as it probably have been canceled along with the failed order */
			if (isDefined(seatingReservation)) await clearSeatingReservation();
			/** open order create error modal */
			openModal('orderCreateError', {
				centered: true,
				closeOnClickOutside: false,
				closeOnEscape: false,
				withCloseButton: false,
				title: t(`${OrderCreateErrorModal.displayName}.title`),
				innerProps: {
					error,
					onButtonClick: async ({ closeModal, id }: { closeModal: (id: string) => void; id: string }) => {
						/** change view to tickets to ensure new reservation creation */
						if (isDefined(seatingReservation)) setCartView(CART_VIEW.TICKETS);
						/** close the modal */
						closeModal(id);
					},
				},
			});
		},
	});

	return { createOrderApi, canCreateOrder, unableToCreateOrderReason };
}

export async function getMaximusProductsApiQueryFn(params: { eventId: UUID; locale: string | undefined; passApi: PassApi<any> }) {
	const { eventId, locale, passApi } = params;

	/** get ticket types from API */
	const res = await passApi.v2Event.getExtraProductsFromEvent.request(
		{ eventId: params.eventId },
		{ language: languageEnumTickets.get(locale, 'code').languageId },
	);

	/** map ticket types to the desired format */
	const maximusProductsConfig = res.data.products.map((product) => createMaximusProductConfig(product, res.data.companies, { eventId }));
	return { maximusProductsConfig };
}

export function useMaximusProductsApi() {
	const cartConfig = useCartStore((s) => s.cartConfig);
	const loadMaximusProductsConfig = useCartStore((s) => s.loadMaximusProductsConfig);
	const router = useRouter();
	const ticketTypesApiQuery = useTicketTypesApiQuery();
	const { passApi } = useApiClient();

	/** maximus products API */
	const maximusProductsApi = useQuery({
		queryKey: cartStoreQueryKeys.getMaximusProductsApiQueryKey(cartConfig.eventId, router.locale),
		async queryFn({ queryKey }) {
			const [, params] = queryKey;
			return getMaximusProductsApiQueryFn({ eventId: params.eventId, locale: params.locale, passApi });
		},
		/** fetch only when ticket types are loaded */
		enabled: ticketTypesApiQuery.hasData,
		refetchOnWindowFocus: false,
		/** 5 minutes stale time */
		staleTime: makeTime('5 minutes'),
	});

	/** load maximus products to cart store */
	React.useEffect(() => {
		if (isDefined(maximusProductsApi.data)) {
			loadMaximusProductsConfig({
				maximusProducts: maximusProductsApi.data.maximusProductsConfig,
				forceOverwrite: true,
				duplicatesStrategy: 'replace',
			});
		}
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [maximusProductsApi.data?.maximusProductsConfig]);

	return { maximusProductsApi };
}
